import React, { useCallback, useEffect, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, Loader2, X, Globe, Eye, Copy, CheckCircle, RefreshCcw, Link as LinkIcon, AlertTriangle, Info, RefreshCw, Play, Pause } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import type { User } from '@/types/user';
import { useAuth } from '@/contexts/useAuth';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useCurrentUser, useUserMutations, useBranchOffices } from '@/hooks/useProfiles';
import { useSupabase } from '@/hooks/useSupabase';
import { formatPhoneNumber } from '@/utils/phoneFormatting';
import { createComponentLogger } from '@/utils/logger';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { DualPhotoUpload } from './DualPhotoUpload';
import { WebsiteTemplateGallery } from './website/WebsiteTemplateGallery';
import { ProfilePublicationPanel } from './website/ProfilePublicationPanel';
import { ProfileSeoApprovalPanel } from './website/ProfileSeoApprovalPanel';
import { ProfileDeliveryPanel } from './website/ProfileDeliveryPanel';
import { usePublicProfileDelivery } from '@/hooks/usePublicProfileDelivery';
import {
  isCompanyComplianceReady,
  type PublicProfileCompanyCompliance
} from './website/profileReadiness';
import type { ProfileData } from '@/apps/loan-officer-profile/hooks/useProfileData';
import {
  ProfileCredentialIntegrations,
  type ProfileManagementMode
} from './ProfileCredentialIntegrations';
import { TwilioSmsCredentials } from './TwilioSmsCredentials';
import { VoiceStudio } from './VoiceStudio';
import { ProfileSlugService } from '@/services/profileSlugService';
import { featureFlagService } from '@/services/featureFlagService';
import {
  marketingAuthorizationService,
  type MarketingAuthorization
} from '@/services/marketingAuthorizationService';
import { notificationService } from '@/services/notificationService';
import { setUserEmploymentStatusWithEncompass } from '@/services/encompassUserLifecycleService';
import { ObChangePlanDialog } from '../compensation/ObChangePlanDialog';
import { ObInitialAssignDialog } from '../compensation/ObInitialAssignDialog';
import {
  inspectObUser,
  refreshObIdentity,
  repairMissingCore,
  CompensationServiceError,
  formatCompRequestStatus,
  type ObInspectResult
} from '@/services/compensationService';
import { ObCompRequestsPanel } from '../compensation/ObCompRequestsPanel';
import {
  marshalUserProfileNumericFields,
  normalizeUserProfileNumericInput
} from './userProfileNumericFields';
import { deriveYearsOfExperience } from './userProfileExperience';
import {
  countProfileBioWords,
  PROFILE_BIO_INDEXING_WORD_FLOOR,
  repairProfileBioLineBreakArtifacts
} from './profileBioGuidance';

// 14 standard core OB product categories — duplicated from
// supabase/functions/_shared/comp-utils.ts:52 because client + edge function
// can't share a module. Kept inline here (single client consumer); if a second
// client file ever needs it, extract to src/constants/obComp.ts.
const STANDARD_CORE_PRODUCT_CATEGORIES_CLIENT: readonly string[] = [
  'ConformingFixed', 'ConformingArm', 'FhaFixed', 'FhaArm', 'VaFixed', 'VaArm',
  'Usda', 'NonConformingFixed', 'NonConformingArm', 'HelocFixed', 'HelocArm',
  'SecondMortgageFixed', 'SecondMortgageArm', 'Other'
] as const;
import { titleEscrowService } from '@/services/titleEscrowService';
import type { TitleEscrowCompany, GroupedFeeTiers } from '@/types/title-escrow';
import { CompanyFeePreview } from './CompanyFeePreview';
import { MultiSelect } from './MultiSelect';
import { SignatureCapture, type SignatureMetadata } from '@/components/common/SignatureCapture';
import { PendingChangesCard } from './PendingChangesCard';
import { ConfigureGmailSignature } from './ConfigureGmailSignature';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { UserProfileIdentityHeader } from './UserProfileIdentityHeader';
import { resolveEmployeeDocumentsProfileMode } from './employeeDocumentsAccess';

const log = createComponentLogger('UserFormEnhanced', 'user-management');
const EmployeeDocumentsTab = React.lazy(async () => {
  const module = await import('./EmployeeDocumentsTab');
  return { default: module.EmployeeDocumentsTab };
});

// Role options with consistent color mapping
const ROLE_OPTIONS = [
  { value: 'Admin', label: 'Admin', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'Manager', label: 'Manager', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'Compliance Specialist', label: 'Compliance Specialist', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  { value: 'Loan Officer', label: 'Loan Officer', color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'Loan Officer Assistant', label: 'Loan Officer Assistant', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'Processing', label: 'Processing', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'Underwriter', label: 'Underwriter', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'Closer', label: 'Closer', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'Partner', label: 'Partner', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'Recruiter', label: 'Recruiter', color: 'bg-violet-100 text-violet-700 border-violet-200' }
];

const PARTNER_ROLE_OPTIONS = ROLE_OPTIONS.filter(option =>
  ['Manager', 'Compliance Specialist', 'Loan Officer', 'Loan Officer Assistant', 'Processing', 'Underwriter', 'Closer']
    .includes(option.value)
);

const ROLE_LABEL_TO_DB: Record<string, string> = {
  'Loan Officer': 'loan_officer',
  'Loan Officer Assistant': 'loan_officer_assistant',
  Processing: 'processor',
  Underwriter: 'underwriter',
  Manager: 'manager',
  Admin: 'admin',
  Closer: 'closer',
  'Compliance Specialist': 'compliance_specialist',
  Partner: 'partner',
  Recruiter: 'recruiter'
};

const ROLE_DB_TO_LABEL: Record<string, string> = {
  loan_officer: 'Loan Officer',
  loan_officer_assistant: 'Loan Officer Assistant',
  processor: 'Processing',
  processing: 'Processing',
  underwriter: 'Underwriter',
  manager: 'Manager',
  admin: 'Admin',
  closer: 'Closer',
  compliance_specialist: 'Compliance Specialist',
  partner: 'Partner',
  recruiter: 'Recruiter'
};

const formatRoleForDisplay = (role: string) => ROLE_DB_TO_LABEL[role] || role;

const formatRoleForDb = (role: string) =>
  ROLE_LABEL_TO_DB[role] || role.toLowerCase().replace(/ /g, '_');

const sortRoles = (roles: string[]) => [...roles].map(formatRoleForDb).sort();

const areRolesEqual = (a: string[] = [], b: string[] = []) => {
  const sortedA = sortRoles(a);
  const sortedB = sortRoles(b);
  return sortedA.length === sortedB.length && sortedA.every((role, index) => role === sortedB[index]);
};

const areRoleSequencesEqual = (a: string[] = [], b: string[] = []) => {
  const normalizedA = a.map(formatRoleForDb);
  const normalizedB = b.map(formatRoleForDb);
  return normalizedA.length === normalizedB.length
    && normalizedA.every((role, index) => role === normalizedB[index]);
};

type PartnerProfileField =
  | 'firstName'
  | 'lastName'
  | 'phone'
  | 'mobilePhone'
  | 'officeExtension'
  | 'branchName'
  | 'branchAddress'
  | 'addressPreference'
  | 'title'
  | 'bio'
  | 'yearsOfExperience'
  | 'nmlsNumber'
  | 'stateLicenseNumber'
  | 'licensedStates'
  | 'specialties'
  | 'mortgageSpecialties'
  | 'website'
  | 'websiteTemplate'
  | 'linkedinUrl'
  | 'facebookUrl'
  | 'instagramUrl'
  | 'tiktokUrl'
  | 'calendarLink'
  | 'mobileDisplayConsent'
  | 'preferredTitleCompanyId'
  | 'preferredEscrowCompanyId';

type PartnerProfileData = {
  [Field in PartnerProfileField]?: User[Field] | null;
};

const buildPartnerProfileUpdates = (
  profileData: PartnerProfileData,
  changedFields?: Partial<Record<keyof User, unknown>>
) => {
  const updates: Record<string, unknown> = {};
  const assign = (key: string, value: unknown, changedField?: keyof User) => {
    if (value !== undefined && (!changedField || Boolean(changedFields?.[changedField]))) {
      updates[key] = value;
    }
  };

  assign('first_name', profileData.firstName);
  assign('last_name', profileData.lastName);
  assign('phone', profileData.phone);
  assign('mobile_phone', profileData.mobilePhone);
  assign('office_extension', profileData.officeExtension);
  assign('branch_name', profileData.branchName);
  assign('branch_address', profileData.branchAddress);
  assign('address_preference', profileData.addressPreference);
  assign('title', profileData.title);
  assign('bio', profileData.bio);
  assign('years_of_experience', profileData.yearsOfExperience);
  assign('nmls_number', profileData.nmlsNumber);
  assign('state_license_number', profileData.stateLicenseNumber);
  assign('license_states', profileData.licensedStates);
  assign('specialties', profileData.specialties);
  assign('mortgage_specialties', profileData.mortgageSpecialties);
  assign('website', profileData.website);
  assign('website_template', profileData.websiteTemplate, 'websiteTemplate');
  assign('linkedin_url', profileData.linkedinUrl);
  assign('facebook_url', profileData.facebookUrl);
  assign('instagram_url', profileData.instagramUrl);
  assign('tiktok_url', profileData.tiktokUrl);
  assign('calendar_link', profileData.calendarLink);
  assign('mobile_display_consent', profileData.mobileDisplayConsent);
  assign('preferred_title_company_id', profileData.preferredTitleCompanyId, 'preferredTitleCompanyId');
  assign('preferred_escrow_company_id', profileData.preferredEscrowCompanyId, 'preferredEscrowCompanyId');

  return updates;
};

function formatPublicProfileAddress(address?: {
  street?: string | null;
  unit?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
} | null): string {
  if (!address) {
    return '';
  }
  const street = [address.street, address.unit || address.suite].filter(Boolean).join(', ');
  const locality = [address.city, address.state].filter(Boolean).join(', ');
  return [street, locality, address.zip].filter(Boolean).join(' ');
}

// Banked compensation dropdown options
const BANKED_COMPENSATION_OPTIONS = [
  { value: '0.75', label: '0.75%' },
  { value: '1.00', label: '1.00%' },
  { value: '1.25', label: '1.25%' },
  { value: '1.50', label: '1.50%' },
  { value: '1.75', label: '1.75%' },
  { value: '2.00', label: '2.00%' },
  { value: '2.25', label: '2.25%' },
  { value: '2.50', label: '2.50%' },
  { value: '2.75', label: '2.75%' },
  { value: '3.00', label: '3.00%' }
];

type UserProfileFormValues = Partial<User> & {
  status?: 'Pending' | 'Active' | 'Terminated';
  branch_name?: string;
  branch_address?: User['branchAddress'];
};

interface BioCleanupNotice {
  cleanedValue: string;
  rawValue: string;
  repairedBreaks: number;
  source: 'manual' | 'paste';
  cursorPosition: number;
}

interface UserFormEnhancedProps {
  userId?: string;
  onSuccess?: () => void;
  isAdminView?: boolean; // Show all tabs and features
  managementMode?: ProfileManagementMode;
  showBranchSelection?: boolean; // Show branch dropdown
  showCompensation?: boolean; // Admin compensation tab — admin tooling (Refresh/Setup/Repair) and admin init effects
  showSelfCompensation?: boolean; // Self-serve compensation tab on /my-profile
}

export const UserFormEnhanced: React.FC<UserFormEnhancedProps> = ({
  userId,
  onSuccess,
  isAdminView = false,
  managementMode,
  showCompensation = false,
  showSelfCompensation = false
}) => {
  const { user } = useAuth();
  const supabase = useSupabase(); // Get authenticated Supabase client

  // Use hooks for data fetching
  const currentProfileHook = useCurrentUser();
  const employeeDocumentsFlag = useFeatureFlag('employee_documents_v1');
  const { updateProfile, createProfile, updating } = useUserMutations();
  const { branches, loading: _branchesLoading } = useBranchOffices();
  const effectiveManagementMode = managementMode ?? (isAdminView ? 'admin' : 'self');
  const isAdminMode = effectiveManagementMode === 'admin' || isAdminView;
  const isPartnerDelegatedMode = effectiveManagementMode === 'partner_delegated';
  const canManageUser = isAdminMode || isPartnerDelegatedMode;
  const targetProfileUserId = userId || user?.id || '';
  const isOwnProfile = Boolean(user?.id) && targetProfileUserId === user?.id;
  const employeeDocumentsMode = resolveEmployeeDocumentsProfileMode({
    featureEnabled: employeeDocumentsFlag.enabled,
    managementMode: effectiveManagementMode,
    isAdminView: isAdminMode,
    isOwnProfile,
    actorRoles: currentProfileHook.profile?.roles
  });
  const roleOptions = isPartnerDelegatedMode ? PARTNER_ROLE_OPTIONS : ROLE_OPTIONS;

  // State for specific user profile (when userId provided)
  const [specificProfile, setSpecificProfile] = useState<User | null>(null);
  const [loadingSpecific, setLoadingSpecific] = useState(false);

  // Determine which profile to use
  const profile = userId ? specificProfile : currentProfileHook.profile;
  const loading = userId ? loadingSpecific : currentProfileHook.loading;
  const error = userId ? null : currentProfileHook.error;

  const [_selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [licenseStates, setLicenseStates] = useState<string[]>([]);
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newState, setNewState] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState<string>('');
  const [generatingUrl, setGeneratingUrl] = useState(false);
  const [generatingApplicationLink, setGeneratingApplicationLink] = useState(false);
  const [publicationUpdating, setPublicationUpdating] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [voiceStudioOpen, setVoiceStudioOpen] = useState(false);
  const [voicePreviewPlaying, setVoicePreviewPlaying] = useState(false);
  const voicePreviewRef = React.useRef<HTMLAudioElement>(null);
  const voiceStudioTriggerRef = React.useRef<HTMLButtonElement>(null);
  const bioTextareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [bioCleanupNotice, setBioCleanupNotice] = useState<BioCleanupNotice | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['Loan Officer']); // Default to Loan Officer
  const [, setPrimaryRole] = useState<string>('loan_officer');
  const [employmentStatus, setEmploymentStatus] = useState<'Pending' | 'Active' | 'Terminated'>('Pending');
  const [activeTab, setActiveTab] = useState<string>('personal'); // Track active tab
  const [mortgageSpecialties, setMortgageSpecialties] = useState<string[]>([]);
  const [addressType, setAddressType] = useState<'corporate' | 'custom'>('corporate');
  const [customAddress, setCustomAddress] = useState({
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: ''
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
  const [organizationAddress, setOrganizationAddress] = useState<any>(null);
  const [publicProfileCompany, setPublicProfileCompany] = useState<PublicProfileCompanyCompliance | null>(null);
  const [marketingGateEnabled, setMarketingGateEnabled] = useState(false);
  const [marketingAuthorization, setMarketingAuthorization] = useState<MarketingAuthorization | null>(null);
  const [marketingAuthorizationLoading, setMarketingAuthorizationLoading] = useState(true);
  const [marketingAuthorizationError, setMarketingAuthorizationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const marketingLocked = marketingGateEnabled
    && (
      Boolean(marketingAuthorizationError)
      || !marketingAuthorization
      || (marketingAuthorization.governedByRecruiting && !marketingAuthorization.authorized)
    );
  const marketingControlsLocked = marketingAuthorizationLoading || marketingLocked;
  const marketingLockDescriptionId = 'nmls-marketing-lock-description';

  const ensureMarketingActionAllowed = () => {
    if (!marketingControlsLocked) {
      return true;
    }

    toast.error(
      marketingAuthorizationLoading
        ? 'NMLS authorization is still being verified'
        : 'Marketing is locked until NMLS authorization is recorded'
    );
    return false;
  };

  // Helper to convert db employment_status to UI format
  const toUiStatus = (dbStatus?: string): 'Pending' | 'Active' | 'Terminated' => {
    const statusMap: Record<string, 'Pending' | 'Active' | 'Terminated'> = {
      pending: 'Pending',
      active: 'Active',
      terminated: 'Terminated'
    };
    return statusMap[dbStatus || ''] || 'Pending';
  };

  // Compensation state (for admin editing loan officers)
  const [compensationData, setCompensationData] = useState({
    maximum_compensation: null as number | null,
    banked_compensation: null as number | null,
    broker_commission_split: null as number | null,
    banked_override_bps: null as number | null,
    broker_mentor_commission_split: null as number | null,
    override_recipient_id: null as string | null,
    mentor_id: null as string | null,
    mentorship_active: false,
    brokered_deals_completed: 0
  });
  const [, setPotentialMentors] = useState<Array<{id: string; first_name: string; last_name: string}>>([]);

  // Pending compensation request state (from users table columns)
  const [pendingCompensationRequest, setPendingCompensationRequest] = useState<{
    pending_banked_compensation: number | null;
    pending_maximum_compensation: number | null;
    compensation_request_justification: string | null;
    compensation_request_effective_date: string | null;
    compensation_request_submitted_at: string | null;
  } | null>(null);

  // Request change modal state
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestForm, setRequestForm] = useState({
    bankedCompensation: null as number | null,
    maximumCompensation: null as number | null,
    justification: ''
  });
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // OB compensation feature flag + new flow state (plan v5)
  const [obCompFeatureEnabled, setObCompFeatureEnabled] = useState(false);
  const [obChangePlanDialogOpen, setObChangePlanDialogOpen] = useState(false);
  const [obOpenRequest, setObOpenRequest] = useState<{
    id: string;
    target_schedule_name: string;
    target_percentage: number | null;
    target_max_cap: number | null;
    effective_date: string;
    status: 'pending' | 'approved' | 'needs_review' | 'failed';
    last_failure_reason: string | null;
  } | null>(null);

  // Admin-only OB inspect/setup flow (2026-04-18):
  // - `obInspectResult` is kept in local state as the UI source of truth for the
  //   non-cached branches (missing_plan / future_dated_only / mixed / custom_only),
  //   because users.optimal_blue_current_* only reflects canonical state for
  //   core/full_footprint and ob_comp_assignments is append-only with no tombstone.
  // - After every inspect/assign we also refetch the profile so the freshness
  //   timestamp and (for core/full_footprint) the canonical cache fields render
  //   fresh. When inspectResult is null the card falls back to the cached render.
  const [obInspecting, setObInspecting] = useState(false);
  const [obInspectResult, setObInspectResult] = useState<ObInspectResult | null>(null);
  const [obInitialAssignOpen, setObInitialAssignOpen] = useState(false);
  const [obRepairing, setObRepairing] = useState(false);
  // Reset inspect state when the viewed user changes so the previous user's
  // needs_review / missing_plan / future_dated_only panel can't leak into the
  // next user's card. Keying on both userId (admin path) and profile.id
  // (self path) covers both render modes.

  // Xactus submission states
  const [xactusSubmitting, setXactusSubmitting] = useState(false);
  const [xactusStatus, setXactusStatus] = useState<'preparing' | 'submitting' | 'success' | 'manual' | 'error'>('preparing');
  const [xactusModalOpen, setXactusModalOpen] = useState(false);
  const [xactusIframeUrl, setXactusIframeUrl] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
  const [xactusHistory, setXactusHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Title/Escrow company preferences state
  const [titleEscrowCompanies, setTitleEscrowCompanies] = useState<TitleEscrowCompany[]>([]);
  const [selectedTitleCompanyId, setSelectedTitleCompanyId] = useState<string | null>(null);
  const [selectedEscrowCompanyId, setSelectedEscrowCompanyId] = useState<string | null>(null);
  const [titleFees, setTitleFees] = useState<GroupedFeeTiers[]>([]);
  const [escrowFees, setEscrowFees] = useState<GroupedFeeTiers[]>([]);
  const [titleFeesOpen, setTitleFeesOpen] = useState(false);
  const [escrowFeesOpen, setEscrowFeesOpen] = useState(false);
  const [loadingTitleEscrow, setLoadingTitleEscrow] = useState(true);

  // Default signature state
  const [showSignatureCapture, setShowSignatureCapture] = useState(false);
  const [savingSignature, setSavingSignature] = useState(false);

  // Initialize form before using any of its methods
  const { register, handleSubmit, reset, resetField, setValue, watch, formState: { errors, isDirty, dirtyFields } } = useForm<UserProfileFormValues>({
    mode: 'onChange', // Track changes as user types
    defaultValues: profile || {} // Initialize with profile data
  });
  const bioRegistration = register('bio');

  // Delivery state for the public per-officer page. The permanent URL is
  // surfaced only once the published site manifest acknowledges it; until
  // then the directory anchor stays the shareable link.
  const publicProfileSlug = String(watch('websiteSlug') || profile?.websiteSlug || '');
  const publicProfilePublished = Boolean(watch('websiteEnabled') ?? profile?.websiteEnabled);
  const profileDelivery = usePublicProfileDelivery(
    publicProfileSlug || null,
    publicProfilePublished
  );

  // Helper to get changed fields for the pending changes card
  const getChangedFields = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
    const changes: any[] = [];
    const currentValues = watch();

    Object.keys(dirtyFields).forEach(field => {
      let oldValue: unknown = profile?.[field as keyof User];
      let newValue: unknown = currentValues[field as keyof User];

      // These two RHF fields use display values while the profile stores
      // employmentStatus plus snake_case role values. Normalize both sides so
      // the pending-change card never exposes storage vocabulary.
      if (field === 'status') {
        oldValue = toUiStatus(profile?.employmentStatus);
        newValue = employmentStatus;
      } else if (field === 'roles') {
        if (areRoleSequencesEqual(profile?.roles, selectedRoles)) {
          return;
        }
        oldValue = (profile?.roles ?? []).map(formatRoleForDisplay);
        newValue = selectedRoles.map(formatRoleForDisplay);
      }

      if (oldValue !== newValue) {
        changes.push({
          field,
          oldValue,
          newValue
        });
      }
    });

    return changes;
  };
  const pendingChanges = getChangedFields();
  const hasPendingChanges = isDirty && pendingChanges.length > 0;

  // Register application fields on mount
  useEffect(() => {
    register('applicationLink');
    register('applicationLinkGeneratedAt');
    register('roles');
    register('status');
    register('licensedStates');
    register('mortgageSpecialties');
    register('preferredTitleCompanyId');
    register('preferredEscrowCompanyId');
  }, [register]);

  // Reset the live-inspect state when the viewed user changes. obInspectResult
  // is the UI source of truth for non-cached branches (missing_plan,
  // future_dated_only, mixed_core), so without this reset the previous
  // user's panel could leak into the next user when the component instance
  // stays mounted across admin navigation.
  useEffect(() => {
    setObInspectResult(null);
    setObInspecting(false);
    setObRepairing(false);
  }, [userId, profile?.id]);

  // Track unsaved changes for navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasPendingChanges && !isSaving) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasPendingChanges, isSaving]);

  // Function to fetch corporate address
  const fetchCorporateAddress = async () => {
    try {
      let organizationId = profile?.organizationId;

      if (!organizationId) {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user?.id) {
          return;
        }

        const { data: userRecord } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', userData.user.id)
          .single();

        organizationId = userRecord?.organization_id;
      }

      if (!organizationId) {
        log.info('User has no organization_id set');
        setPublicProfileCompany(null);
        return;
      }

      const { data: companyData, error: companyError } = await supabase
        .from('company_profiles')
        .select('company_name, legal_name, company_nmls_id, state_licenses, headquarters_address, main_phone, main_email, website, logo_url, equal_housing_lender, default_disclaimers')
        .eq('organization_id', organizationId)
        .single();

      if (companyError) {
        log.warn('Failed to load headquarters address', { metadata: { error: companyError.message } });
        setPublicProfileCompany(null);
        setOrganizationAddress({
          street: '',
          suite: '',
          city: '',
          state: '',
          zip: ''
        });
      } else if (companyData) {
        setPublicProfileCompany(companyData as PublicProfileCompanyCompliance);
        const address = companyData.headquarters_address;
        setOrganizationAddress({
          street: address?.street?.trim() || '',
          suite: address?.suite || '',
          city: address?.city || '',
          state: address?.state || '',
          zip: address?.zip || ''
        });
      } else {
        log.info('No headquarters address configured');
        setPublicProfileCompany(null);
        setOrganizationAddress({
          street: '',
          suite: '',
          city: '',
          state: '',
          zip: ''
        });
      }
    } catch (error) {
      log.error('Error fetching corporate address', {}, error as Error);
      setPublicProfileCompany(null);
      setOrganizationAddress({
        street: '',
        suite: '',
        city: '',
        state: '',
        zip: ''
      });
    }
  };

  // Load corporate address on component mount
  useEffect(() => {
    fetchCorporateAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing legacy lint debt (not part of the OB identity fix)
  }, []); // Run once on mount

  // Fetch Xactus request history
  const fetchXactusHistory = async () => {
    try {
      setLoadingHistory(true);
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user?.id) {
return;
}

      const { data: userRecord } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userRecord?.organization_id) {
return;
}

      const { data, error } = await supabase
        .from('users')
        .select('id, first_name, last_name, email, nmls_number, xactus_request_date, xactus_request_status')
        .eq('organization_id', userRecord.organization_id)
        .not('xactus_request_date', 'is', null)
        .order('xactus_request_date', { ascending: false })
        .limit(10);

      if (error) {
        log.error('Failed to fetch Xactus history', {}, error);
      } else {
        setXactusHistory(data || []);
      }
    } catch (error) {
      log.error('Error fetching Xactus history', {}, error as Error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load Xactus history on mount and when tab changes to credentials
  useEffect(() => {
    if (activeTab === 'credentials' && !isPartnerDelegatedMode) {
      fetchXactusHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing legacy lint debt (not part of the OB identity fix)
  }, [activeTab]);

  // Restore only tabs this render actually owns. A saved Compensation tab
  // from a prior LO/admin profile must not blank a four-tab profile.
  useEffect(() => {
    const savedTab = localStorage.getItem('profileFormActiveTab');
    const allowedTabs = new Set([
      'personal',
      'professional',
      'marketing',
      'credentials',
      ...(employeeDocumentsMode ? ['documents'] : []),
      ...((showCompensation || showSelfCompensation) ? ['compensation'] : [])
    ]);
    setActiveTab(savedTab && allowedTabs.has(savedTab) ? savedTab : 'personal');
  }, [employeeDocumentsMode, showCompensation, showSelfCompensation, userId]);

  // Load specific user profile if userId is provided
  useEffect(() => {
    if (userId) {
      const loadSpecificProfile = async () => {
        try {
          setLoadingSpecific(true);
          // Note: We need to use UserService directly here since hooks
          // don't support dynamic userId loading
          const { UserService } = await import('@/services');
          const { data } = await UserService.getUser(userId, supabase);
          if (data) {
            setSpecificProfile(data);

            // Use roles from the already fetched user data
            setSelectedRoles(data.roles?.map(formatRoleForDisplay) || []);
            setPrimaryRole(data.roles?.[0] || 'loan_officer');

            // Use employmentStatus from the already fetched user data
            setEmploymentStatus(toUiStatus(data.employmentStatus));
          }

          // Load corporate address for admin view too!
          await fetchCorporateAddress();

        } catch {
          toast.error('Failed to load profile');
        } finally {
          setLoadingSpecific(false);
        }
      };
      loadSpecificProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing legacy lint debt (not part of the OB identity fix)
  }, [userId]);

  // Load compensation data when profile loads (admin only)
  useEffect(() => {
    if (showCompensation && profile) {
      // Set compensation from profile (raw snake_case fields)
      setCompensationData({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        maximum_compensation: (profile as any).maximum_compensation ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        banked_compensation: (profile as any).banked_compensation ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        broker_commission_split: (profile as any).broker_commission_split ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        banked_override_bps: (profile as any).banked_override_bps ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        broker_mentor_commission_split: (profile as any).broker_mentor_commission_split ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        override_recipient_id: (profile as any).override_recipient_id ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        mentor_id: (profile as any).mentor_id ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        mentorship_active: (profile as any).mentorship_active ?? false,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        brokered_deals_completed: (profile as any).brokered_deals_completed ?? 0
      });

      // Initialize request form with current values
      setRequestForm({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        bankedCompensation: (profile as any).banked_compensation ?? null,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        maximumCompensation: (profile as any).maximum_compensation ?? null,
        justification: ''
      });

      // Check for pending compensation request (from users table columns)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
      const pendingBanked = (profile as any).pending_banked_compensation;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
      const pendingMax = (profile as any).pending_maximum_compensation;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
      const pendingJustification = (profile as any).compensation_request_justification;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
      const pendingEffectiveDate = (profile as any).compensation_request_effective_date;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
      const pendingSubmittedAt = (profile as any).compensation_request_submitted_at;

      if (pendingSubmittedAt) {
        setPendingCompensationRequest({
          pending_banked_compensation: pendingBanked,
          pending_maximum_compensation: pendingMax,
          compensation_request_justification: pendingJustification,
          compensation_request_effective_date: pendingEffectiveDate,
          compensation_request_submitted_at: pendingSubmittedAt
        });
      } else {
        setPendingCompensationRequest(null);
      }

      // Load potential mentors (loan officers)
      const loadPotentialMentors = async () => {
        const { data } = await supabase
          .from('users')
          .select('id, first_name, last_name')
          .eq('is_loan_officer', true)
          .order('last_name');
        if (data) {
          setPotentialMentors(data);
        }
      };
      loadPotentialMentors();
    }
  }, [showCompensation, profile, supabase]);

  // Load OB compensation feature flag + any open compensation_change_requests row.
  // This drives the new (plan v5) request flow that supersedes users.pending_*.
  // Gated on either compensation surface (admin tooling tab OR self-serve tab)
  // so /my-profile gets the same data without a separate effect.
  useEffect(() => {
    if (!(showCompensation || showSelfCompensation) || !profile) {
return;
}
    let cancelled = false;
    void (async () => {
      const targetUserId = userId ?? user?.id;
      // Resolve the flag for the CALLER (the logged-in user), matching the server-side gates
      // in comp-set / comp-request / comp-approve / comp-reject. Resolving for targetUserId
      // here would let per-user overrides on feature_flags_users produce mismatched UI vs
      // 503 behavior. A global feature_flags.enabled flip behaves identically either way.
      const enabled = await featureFlagService.isObCompensationFeatureEnabled(user?.id);
      if (cancelled) {
return;
}
      setObCompFeatureEnabled(enabled);

      if (enabled && targetUserId) {
        const { data, error } = await supabase
          .from('compensation_change_requests')
          .select('id, target_schedule_name, target_percentage, target_max_cap, effective_date, status, last_failure_reason')
          .eq('target_user_id', targetUserId)
          .in('status', ['pending', 'approved', 'needs_review', 'failed'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) {
return;
}
        if (error) {
          log.warn('Failed to load open compensation_change_request', { targetUserId, message: error.message });
          setObOpenRequest(null);
          return;
        }
        setObOpenRequest((data as typeof obOpenRequest) ?? null);
      } else {
        setObOpenRequest(null);
      }
    })();
    return () => {
 cancelled = true; 
};
  }, [showCompensation, showSelfCompensation, profile, userId, user?.id, supabase]);

  // Load roles for current user (when not editing specific user)
  useEffect(() => {
    if (!userId && currentProfileHook.profile) {
      const profile = currentProfileHook.profile;

      // Use data from the already loaded profile
      const formattedRoles = profile.roles?.map(formatRoleForDisplay) || [];
      setSelectedRoles(formattedRoles);
      setValue('roles', formattedRoles, { shouldDirty: false });

      setPrimaryRole(profile.roles?.[0] || 'loan_officer');

      const currentStatus = toUiStatus(profile.employmentStatus);
      setEmploymentStatus(currentStatus);
      setValue('status', currentStatus, { shouldDirty: false });

      // Load company headquarters address
      fetchCorporateAddress();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing legacy lint debt (not part of the OB identity fix)
  }, [userId, currentProfileHook.profile]);

  // Hydrate saved profile data independently of the asynchronous branch lookup.
  useEffect(() => {
    if (profile) {
      setBioCleanupNotice(null);
      setSpecialties(profile.specialties || []);
      setLicenseStates(profile.licensedStates || []);

      const specialties = profile.mortgageSpecialties || [];
      setMortgageSpecialties(specialties);
      setValue('mortgageSpecialties', specialties, { shouldDirty: false });

      // Load address preference from stored value (no more guessing!)
      if (profile.addressPreference) {
        setAddressType(profile.addressPreference);
      } else {
        // Default to corporate for existing profiles without preference
        setAddressType('corporate');
      }

      // Load custom address if it exists and preference is custom
      if (profile.addressPreference === 'custom' && profile.branchAddress) {
        // Parse branchAddress which could be string or object
        if (typeof profile.branchAddress === 'string') {
          try {
            const parsed = JSON.parse(profile.branchAddress);
            setCustomAddress({
              street: parsed.street || '',
              unit: parsed.unit || parsed.suite || '',
              city: parsed.city || '',
              state: parsed.state || '',
              zip: parsed.zip || ''
            });
          } catch (e) {
            // If parsing fails, leave as empty
            console.error('Failed to parse branch address:', e);
          }
        } else if (profile.branchAddress) {
          setCustomAddress({
            street: profile.branchAddress.street || '',
            unit: profile.branchAddress.unit || profile.branchAddress.suite || '',
            city: profile.branchAddress.city || '',
            state: profile.branchAddress.state || '',
            zip: profile.branchAddress.zip || ''
          });
        }
      }

      // Use profile data directly - it's already in camelCase
      // The transformUserToDB function will handle conversion to snake_case for database
      const formData = {
        ...profile,
        roles: profile.roles?.map(formatRoleForDisplay) || [],
        status: toUiStatus(profile.employmentStatus),
        applicationLink: profile?.applicationLink || '',
        applicationLinkGeneratedAt: profile?.applicationLinkGeneratedAt || ''
      };
      reset(formData, { keepFieldsRef: true });

      // Initialize website template and URL if they exist
      if (profile.websiteTemplate) {
        setSelectedTemplate(profile.websiteTemplate);
      }
      if (profile.websiteSlug) {
        // Use getShareableUrl to always show production domain
        const url = ProfileSlugService.getShareableUrl(profile.websiteSlug);
        setWebsiteUrl(url);
      }
    }
  }, [profile, reset, setValue]);

  // A late branch response must not reset unsaved profile fields.
  useEffect(() => {
    if (profile?.branchName && branches.length > 0) {
      const matchingBranch = branches.find(b => b.branch_name === profile.branchName);
      if (matchingBranch) {
        setSelectedBranchId(matchingBranch._id);
      }
    }
  }, [profile?.branchName, branches]);

  // Load title/escrow companies on mount
  useEffect(() => {
    setLoadingTitleEscrow(true);
    titleEscrowService.getCompanies({ isActive: true })
      .then(setTitleEscrowCompanies)
      .catch((err) => {
        log.error('Failed to load title/escrow companies', {}, err);
      })
      .finally(() => setLoadingTitleEscrow(false));
  }, []);

  // Initialize title/escrow selections from profile
  useEffect(() => {
    if (profile) {
      setSelectedTitleCompanyId(profile.preferredTitleCompanyId || null);
      setSelectedEscrowCompanyId(profile.preferredEscrowCompanyId || null);
    }
  }, [profile]);

  // Load title company fees when selection changes
  useEffect(() => {
    if (selectedTitleCompanyId) {
      titleEscrowService.getGroupedFeeSchedule(selectedTitleCompanyId)
        .then(setTitleFees)
        .catch((err) => {
          log.error('Failed to load title company fees', { companyId: selectedTitleCompanyId }, err);
          setTitleFees([]);
        });
    } else {
      setTitleFees([]);
    }
  }, [selectedTitleCompanyId]);

  // Load escrow company fees when selection changes
  useEffect(() => {
    if (selectedEscrowCompanyId) {
      titleEscrowService.getGroupedFeeSchedule(selectedEscrowCompanyId)
        .then(setEscrowFees)
        .catch((err) => {
          log.error('Failed to load escrow company fees', { companyId: selectedEscrowCompanyId }, err);
          setEscrowFees([]);
        });
    } else {
      setEscrowFees([]);
    }
  }, [selectedEscrowCompanyId]);

  // Show error if profile fails to load
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const refreshMarketingAuthorization = useCallback(async () => {
    if (!targetProfileUserId) {
      setMarketingGateEnabled(false);
      setMarketingAuthorization(null);
      setMarketingAuthorizationError(null);
      setMarketingAuthorizationLoading(false);
      return;
    }

    setMarketingAuthorizationLoading(true);
    setMarketingAuthorizationError(null);
    try {
      const result = await marketingAuthorizationService.getForProfile(
        targetProfileUserId,
        isOwnProfile,
        supabase
      );
      setMarketingGateEnabled(result.gateEnabled);
      setMarketingAuthorization(result.authorization);
    } catch (authorizationError) {
      const message = authorizationError instanceof Error
        ? authorizationError.message
        : 'Unable to verify NMLS Marketing authorization';
      setMarketingGateEnabled(true);
      setMarketingAuthorization(null);
      setMarketingAuthorizationError(message);
    } finally {
      setMarketingAuthorizationLoading(false);
    }
  }, [isOwnProfile, supabase, targetProfileUserId]);

  useEffect(() => {
    void refreshMarketingAuthorization();
  }, [activeTab, profile?.updatedAt, refreshMarketingAuthorization]);

  useEffect(() => {
    const handleAuthorizationChange = (event: Event) => {
      const changedUserId = (event as CustomEvent<{ userId?: string | null }>).detail?.userId;
      if (!changedUserId || changedUserId === targetProfileUserId) {
        void refreshMarketingAuthorization();
      }
    };

    window.addEventListener('lendwise:nmls-authorization-changed', handleAuthorizationChange);
    return () => window.removeEventListener('lendwise:nmls-authorization-changed', handleAuthorizationChange);
  }, [refreshMarketingAuthorization, targetProfileUserId]);

  const _handleBranchChange = (branchId: string) => {
    setSelectedBranchId(branchId);

    if (branchId === 'corporate') {
      // Clear branch fields for corporate office
      setValue('branch_name', 'Corporate Office');
      setValue('branch_address', {
        street: '',
        city: '',
        state: '',
        zip: ''
      });
    } else {
      // Find selected branch and populate fields
      const branch = branches.find(b => b.id === branchId);
      if (branch) {
        setValue('branch_name', branch.branch_name);
        setValue('branch_address', branch.address);
      }
    }
  };

  const _addSpecialty = () => {
    if (newSpecialty && !specialties.includes(newSpecialty)) {
      setSpecialties([...specialties, newSpecialty]);
      setNewSpecialty('');
    }
  };

  const _removeSpecialty = (specialty: string) => {
    setSpecialties(specialties.filter(s => s !== specialty));
  };

  const addLicenseState = () => {
    if (newState && !licenseStates.includes(newState)) {
      const updatedStates = [...licenseStates, newState];
      setLicenseStates(updatedStates);
      setValue('licensedStates', updatedStates, { shouldDirty: true }); // Notify React Hook Form
      setNewState('');
    }
  };

  const removeLicenseState = (state: string) => {
    const updatedStates = licenseStates.filter(s => s !== state);
    setLicenseStates(updatedStates);
    setValue('licensedStates', updatedStates, { shouldDirty: true }); // Notify React Hook Form
  };

  const reservePartnerWebsiteSlug = async (targetUserId: string) => {
    const { data, error } = await supabase.rpc('partner_reserve_managed_user_slug', {
      target_user_id: targetUserId
    });

    if (error) {
      throw error;
    }

    const response = data as {
      website_slug?: string;
    } | null;

    if (!response?.website_slug) {
      throw new Error('Website URL was not returned');
    }

    const url = ProfileSlugService.getShareableUrl(response.website_slug);
    setWebsiteUrl(url);
    setValue('websiteSlug', response.website_slug, { shouldDirty: false });

    return {
      slug: response.website_slug,
      url
    };
  };

  const generateWebsiteUrl = async () => {
    if (!ensureMarketingActionAllowed()) {
      return;
    }

    if (!profile?.firstName || !profile?.lastName) {
      toast.error('Please save your profile first');
      return;
    }

    setGeneratingUrl(true);
    try {
      // Use the authenticated user's ID or the specific userId if editing another user
      const userIdToUse = userId || user?.id;

      if (!userIdToUse) {
        toast.error('User not authenticated');
        return;
      }

      // Slugs are permanent once created: regenerating after a name change would
      // strand every previously shared link (no historical-slug redirect exists).
      const decision = ProfileSlugService.decideWebsiteSlugGeneration(
        profile?.websiteSlug || watch('websiteSlug')
      );
      if (decision.action === 'reuse') {
        setWebsiteUrl(ProfileSlugService.getShareableUrl(decision.slug));
        setValue('websiteSlug', decision.slug);
        toast.success('Website URL is already set');
        return;
      }

      if (isPartnerDelegatedMode) {
        const result = await reservePartnerWebsiteSlug(userIdToUse);
        log.info('Generated partner delegated website slug', { slug: result.slug });
        toast.success('Website URL generated successfully!');
        return;
      }

      const slug = await ProfileSlugService.generateUniqueSlug(
        userIdToUse,
        profile.firstName,
        profile.lastName
      );

      // Reserve the slug in the database
      await ProfileSlugService.reserveSlug(userIdToUse, slug);

      // Use getShareableUrl to always show production domain
      const url = ProfileSlugService.getShareableUrl(slug);
      setWebsiteUrl(url);
      setValue('websiteSlug', slug);
      toast.success('Website URL generated successfully!');
    } catch (error) {
      log.error('Failed to generate website URL', {}, error as Error);
      toast.error('Failed to generate URL');
    } finally {
      setGeneratingUrl(false);
    }
  };

  const generateApplicationLink = async () => {
    if (!ensureMarketingActionAllowed()) {
      return;
    }

    if (isPartnerDelegatedMode) {
      const targetUserId = userId || user?.id;

      if (!targetUserId) {
        toast.error('User ID not found');
        return;
      }

      setGeneratingApplicationLink(true);
      try {
        const { data, error } = await supabase.rpc('partner_generate_managed_user_application_link', {
          target_user_id: targetUserId
        });

        if (error) {
          throw error;
        }

        const response = data as {
          application_link?: string;
          application_link_generated_at?: string;
        } | null;

        if (!response?.application_link || !response.application_link_generated_at) {
          throw new Error('Application link was not returned');
        }

        setValue('applicationLink', response.application_link, {
          shouldDirty: false,
          shouldValidate: true,
          shouldTouch: true
        });
        setValue('applicationLinkGeneratedAt', response.application_link_generated_at, {
          shouldDirty: false,
          shouldValidate: true,
          shouldTouch: true
        });
        toast.success('Application link generated successfully!');
      } catch (error) {
        log.error('Failed to generate partner delegated application link', { userId: targetUserId }, error as Error);
        toast.error(error instanceof Error ? error.message : 'Failed to generate application link');
      } finally {
        setGeneratingApplicationLink(false);
      }
      return;
    }

    const encompassUserId = profile?.encompassUserId;
    if (encompassUserId) {
      const baseUrl = 'https://lendwisemtg.mymortgage-online.com/loan-app/';
      const params = `?siteId=1956469515&lar=${encodeURIComponent(encompassUserId)}&workFlowId=233348`;
      const generatedLink = `${baseUrl}${params}`;
      setValue('applicationLink', generatedLink, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true
      });
      setValue('applicationLinkGeneratedAt', new Date().toISOString(), {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true
      });
      toast.info('Link generated - Remember to save your changes!');
    } else {
      toast.error('Please sync your Encompass account first');
    }
  };

  const copyWebsiteUrl = () => {
    if (!ensureMarketingActionAllowed()) {
      return;
    }

    // Copy whatever is actually safe to share: the permanent per-officer URL
    // only once delivery is acknowledged, the directory anchor otherwise.
    const shareUrl = profileDelivery.state?.shareUrl || websiteUrl;
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setUrlCopied(true);
      toast.success('URL copied to clipboard!');
      setTimeout(() => setUrlCopied(false), 3000);
    }
  };

  const setProfilePublication = async (enabled: boolean) => {
    if (enabled && !ensureMarketingActionAllowed()) {
      return;
    }

    const targetUserId = userId || user?.id;
    if (!targetUserId) {
      toast.error('User ID not found');
      return;
    }

    setPublicationUpdating(true);
    try {
      const { data, error } = await supabase.rpc('set_public_profile_publication', {
        target_user_id: targetUserId,
        p_enabled: enabled
      });
      if (error) {
        throw error;
      }

      const result = data as { website_enabled?: boolean; profile_published_at?: string } | null;
      setValue('websiteEnabled', result?.website_enabled ?? enabled, { shouldDirty: false });
      if (result?.profile_published_at) {
        setValue('profilePublishedAt', result.profile_published_at, { shouldDirty: false });
      }
      if (userId) {
        setSpecificProfile((current) => current ? {
          ...current,
          websiteEnabled: result?.website_enabled ?? enabled,
          profilePublishedAt: result?.profile_published_at || current.profilePublishedAt
        } : current);
      } else {
        await currentProfileHook.refetch();
      }
      toast.success(enabled ? 'Profile published' : 'Profile unpublished');
    } catch (error) {
      log.error('Failed to change public profile publication', { targetUserId, enabled }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to update publication status');
    } finally {
      setPublicationUpdating(false);
    }
  };

  // Handle saving default signature
  const handleSaveDefaultSignature = async (dataUrl: string, metadata: SignatureMetadata) => {
    if (!profile?.id) {
return;
}

    setSavingSignature(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          default_signature_data_url: dataUrl,
          default_signature_method: metadata.method,
          default_signature_font: metadata.font || null,
          default_signature_saved_at: new Date().toISOString()
        })
        .eq('id', profile.id);

      if (error) {
throw error;
}

      toast.success('Default signature saved successfully');
      setShowSignatureCapture(false);
      // Refresh profile data
      await reloadProfileData();
    } catch (error) {
      log.error('Failed to save default signature', { userId: profile.id }, error);
      toast.error('Failed to save signature. Please try again.');
    } finally {
      setSavingSignature(false);
    }
  };

  // Handle removing default signature
  const handleRemoveDefaultSignature = async () => {
    if (!profile?.id) {
return;
}

    setSavingSignature(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          default_signature_data_url: null,
          default_signature_method: null,
          default_signature_font: null,
          default_signature_saved_at: null
        })
        .eq('id', profile.id);

      if (error) {
throw error;
}

      toast.success('Default signature removed');
      // Refresh profile data
      await reloadProfileData();
    } catch (error) {
      log.error('Failed to remove default signature', { userId: profile.id }, error);
      toast.error('Failed to remove signature. Please try again.');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleRequestXactusCredentials = async () => {
    try {
      setXactusSubmitting(true);
      toast.info('Opening Xactus credentials request form...');

      // Get company data
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user?.id) {
        toast.error('User not authenticated');
        setXactusSubmitting(false);
        return;
      }

      const { data: userRecord } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userRecord?.organization_id) {
        toast.error('Organization not found');
        setXactusSubmitting(false);
        return;
      }

      const { data: companyData, error: companyError } = await supabase
        .from('company_profiles')
        .select('company_name, company_nmls_id, headquarters_address')
        .eq('organization_id', userRecord.organization_id)
        .single();

      if (companyError || !companyData) {
        toast.error('Failed to load company information');
        setXactusSubmitting(false);
        return;
      }

      // Validate required user data
      if (!profile?.firstName || !profile?.lastName) {
        toast.error('Please save your profile with first and last name first');
        setXactusSubmitting(false);
        return;
      }

      if (!profile?.nmlsNumber) {
        toast.error('NMLS number is required. Please update your profile.');
        setXactusSubmitting(false);
        return;
      }

      // Get address components
      const address = companyData.headquarters_address;

      // Build form data
      const formData = {
        // Company Information
        companyName: companyData.company_name || '',
        mainCompany: 'X-25455',
        companyNmls: companyData.company_nmls_id || '',
        'companyAddress[addr_line1]': address?.street || '',
        'companyAddress[addr_line2]': address?.suite || '',
        'companyAddress[city]': address?.city || '',
        'companyAddress[state]': address?.state || '',
        'companyAddress[postal]': address?.zip || '',

        // Requester Information
        requesterName: 'Jennifer Steinberg',
        requesterTitle: 'VP',
        requesterPhone: '805-791-4548',
        requestorEmail: 'Jennifer@lendwisemtg.com',
        typeA: 'Tara French',
        strategicAccount: '',

        // Loan Officer Information
        'loName[first]': profile.firstName,
        'loName[last]': profile.lastName,
        loNmls: profile.nmlsNumber || '',
        loPhone: profile.mobilePhone || profile.phone || '',
        loEmail: profile.email || '',

        // Signature/Authorization - Use logged-in user's info (person making request)
        printYour: `${currentProfileHook.profile?.firstName || ''} ${currentProfileHook.profile?.lastName || ''}`,
        'printedName[first]': currentProfileHook.profile?.firstName || '',
        'printedName[last]': currentProfileHook.profile?.lastName || '',
        title: currentProfileHook.profile?.title || 'Authorized User'
      };

      // Create pre-filled form URL
      const params = new URLSearchParams(formData);
      const xactusUrl = `https://xactus.jotform.com/242534618330048?${params.toString()}`;

      // Open pre-filled form in iframe
      setXactusModalOpen(true);
      setXactusStatus('manual');
      setXactusIframeUrl(xactusUrl);

      toast.success('Form pre-filled! Please click Submit at the bottom.', {
        duration: 5000
      });

      // Track request in database
      await supabase
        .from('users')
        .update({
          xactus_request_date: new Date().toISOString(),
          xactus_request_status: 'pending'
        })
        .eq('id', userData.user.id);

      // Reload history
      fetchXactusHistory();

    } catch (error) {
      console.error('Error requesting Xactus credentials:', error);
      toast.error('Failed to open Xactus form. Please try again.');
    } finally {
      setXactusSubmitting(false);
    }
  };

  // Handle saving compensation data (admin uses separate User Compensation page)
  const _handleSaveCompensation = async () => {
    if (!userId) {
      return;
    }

    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('users')
        .update({
          maximum_compensation: compensationData.maximum_compensation,
          banked_compensation: compensationData.banked_compensation,
          broker_commission_split: compensationData.broker_commission_split,
          broker_mentor_commission_split: compensationData.broker_mentor_commission_split,
          mentor_id: compensationData.mentor_id,
          mentorship_active: compensationData.mentor_id !== null
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      toast.success('Compensation updated successfully');
      log.info('Compensation updated', { userId });
    } catch (error) {
      log.error('Failed to update compensation', { userId }, error as Error);
      toast.error('Failed to update compensation');
    } finally {
      setIsSaving(false);
    }
  };

  // Calculate effective date (1st of next month)
  const calculateEffectiveDate = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  };

  // Handle compensation change request submission (self-view only)
  const handleSubmitCompensationRequest = async () => {
    if (!userId || !profile) {
      return;
    }

    // Validate that at least one value changed
    const bankedChanged = requestForm.bankedCompensation !== compensationData.banked_compensation;
    const maxChanged = requestForm.maximumCompensation !== compensationData.maximum_compensation;

    if (!bankedChanged && !maxChanged) {
      toast.error('Please change at least one compensation value');
      return;
    }

    if (!requestForm.justification.trim()) {
      toast.error('Please provide a justification for your request');
      return;
    }

    try {
      setSubmittingRequest(true);
      const effectiveDate = calculateEffectiveDate();

      // Update the users table with pending request columns
      const { error } = await supabase
        .from('users')
        .update({
          pending_banked_compensation: bankedChanged ? requestForm.bankedCompensation : null,
          pending_maximum_compensation: maxChanged ? requestForm.maximumCompensation : null,
          compensation_request_justification: requestForm.justification,
          compensation_request_effective_date: effectiveDate.toISOString().split('T')[0],
          compensation_request_submitted_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (error) {
        throw error;
      }

      // Send email notification to admins
      await notificationService.sendCompensationChangeRequestEmail({
        requesterName: `${profile.firstName} ${profile.lastName}`,
        requesterEmail: profile.email,
        currentBankedComp: compensationData.banked_compensation ? `${compensationData.banked_compensation}%` : null,
        requestedBankedComp: bankedChanged ? `${requestForm.bankedCompensation}%` : null,
        currentMaxComp: compensationData.maximum_compensation ? `$${compensationData.maximum_compensation.toLocaleString()}` : null,
        requestedMaxComp: maxChanged ? `$${requestForm.maximumCompensation?.toLocaleString()}` : null,
        justification: requestForm.justification,
        effectiveDate: effectiveDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      });

      // Update local state
      setPendingCompensationRequest({
        pending_banked_compensation: bankedChanged ? requestForm.bankedCompensation : null,
        pending_maximum_compensation: maxChanged ? requestForm.maximumCompensation : null,
        compensation_request_justification: requestForm.justification,
        compensation_request_effective_date: effectiveDate.toISOString().split('T')[0],
        compensation_request_submitted_at: new Date().toISOString()
      });

      toast.success('Compensation change request submitted');
      setRequestModalOpen(false);
      log.info('Compensation change request submitted', { userId });
    } catch (error) {
      log.error('Failed to submit compensation request', { userId }, error as Error);
      toast.error('Failed to submit compensation request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  const onSubmit = async (data: UserProfileFormValues) => {
    try {
      setIsSaving(true);

      // Form submission processing
      // Validate required fields
      if (!data.firstName || data.firstName.trim() === '') {
        toast.error('First name is required');
        return;
      }

      if (!data.lastName || data.lastName.trim() === '') {
        toast.error('Last name is required');
        return;
      }

      // Require at least one phone number (mobile or office)
      const hasValidMobile = data.mobilePhone && data.mobilePhone.replace(/\D/g, '').length === 10;
      const hasValidOffice = data.phone && data.phone.replace(/\D/g, '').length === 10;

      if (!hasValidMobile && !hasValidOffice) {
        toast.error('Please enter at least one valid 10-digit phone number (mobile or office)');
        return;
      }

      if (!data.email || !data.email.includes('@')) {
        toast.error('Valid email address is required');
        return;
      }

      // Validate NMLS for loan officers only (LOAs work under a licensed LO)
      const requiresNMLS = selectedRoles.some(role =>
        role === 'Loan Officer' ||
        role === 'loan_officer'
      );

      if (requiresNMLS && !data.nmlsNumber) {
        toast.error('NMLS number is required for Loan Officers');
        return;
      }

      // Remove any fields that shouldn't be updated
      const {
        id: _id,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        organizationId: _organizationId,
        mobileDisplayConsent: _mobileDisplayConsent,
        ...updateableData
      } = data;

      // Ensure branchAddress is properly formatted
      if (updateableData.branchAddress) {
        // If it's a string, parse it
        if (typeof updateableData.branchAddress === 'string') {
          try {
            updateableData.branchAddress = JSON.parse(updateableData.branchAddress);
          } catch {
            // Invalid JSON, reset to empty object
            updateableData.branchAddress = {
              street: '',
              city: '',
              state: '',
              zip: ''
            };
          }
        }
      }

      // Build the profile data object - already in camelCase
      // The transformUserToDB function will handle conversion to snake_case
      const marshalledProfileData = marshalUserProfileNumericFields({
        ...updateableData,
        mortgageIndustryStartYear: data.mortgageIndustryStartYear,
        yearsOfExperience: data.yearsOfExperience,
        roles: selectedRoles.map(formatRoleForDb),
        specialties,
        licensedStates: licenseStates,
        mortgageSpecialties,
        addressPreference: addressType,
        // Only store branchAddress if custom is selected
        branchAddress: addressType === 'custom' ? customAddress : null,
        // Explicitly include application fields
        applicationLink: data.applicationLink || null,
        applicationLinkGeneratedAt: data.applicationLinkGeneratedAt || null
      });
      const profileData = {
        ...marshalledProfileData,
        yearsOfExperience: deriveYearsOfExperience(
          marshalledProfileData.mortgageIndustryStartYear
        )
      };

      // Profile data prepared

      const targetUserId = userId || user?.id || '';

	      if (!targetUserId) {
	        toast.error('User ID not found');
	        return;
	      }

	      if (isPartnerDelegatedMode) {
	        if (!profile) {
	          toast.error('User profile not found');
	          return;
	        }

	        const partnerUpdates = buildPartnerProfileUpdates(profileData, dirtyFields);
	        const hasProfileUpdates = Object.keys(partnerUpdates).length > 0;

	        if (hasProfileUpdates) {
	          const { error: partnerUpdateError } = await supabase.rpc('partner_update_managed_user', {
	            target_user_id: targetUserId,
	            updates: partnerUpdates
	          });

	          if (partnerUpdateError) {
	            log.error('Partner delegated profile update failed', { userId: targetUserId }, partnerUpdateError);
	            toast.error(partnerUpdateError.message || 'Failed to update user profile');
	            return;
	          }
	        }

	        const formattedRoles = selectedRoles.map(formatRoleForDb);
	        if (formattedRoles.length > 0 && !areRolesEqual(formattedRoles, profile.roles || [])) {
	          const { error: rolesError } = await supabase.rpc('partner_update_managed_user_roles', {
	            target_user_id: targetUserId,
	            requested_roles: formattedRoles
	          });

	          if (rolesError) {
	            log.error('Partner delegated role update failed', { userId: targetUserId }, rolesError);
	            toast.error(rolesError.message || 'Failed to update user roles');
	            return;
	          }
	        }

	        const normalizedEmploymentStatus = employmentStatus.toLowerCase() as 'active' | 'terminated';
	        if (normalizedEmploymentStatus !== profile.employmentStatus) {
	          const statusResult = await setUserEmploymentStatusWithEncompass(
	            targetUserId,
	            normalizedEmploymentStatus
	          );

	          if (statusResult.warning) {
	            toast.warning(`Employment status updated, but Encompass access was not updated: ${statusResult.warning}`);
	          }
	        }

	        const newTemplate = profileData.websiteTemplate || selectedTemplate;
	        const existingSlug = profile?.websiteSlug || watch('websiteSlug');
	        const shouldAutoGenerateWebsiteSlug = Boolean(dirtyFields.websiteTemplate);

	        if (!marketingControlsLocked && shouldAutoGenerateWebsiteSlug && newTemplate && newTemplate !== 'none' && !existingSlug) {
	          try {
	            const result = await reservePartnerWebsiteSlug(targetUserId);
	            log.info('Auto-generated partner delegated website slug on template selection', {
	              slug: result.slug,
	              template: newTemplate
	            });
	            toast.success('Website URL generated automatically!');
	          } catch (slugError) {
	            log.warn('Failed to auto-generate partner delegated website slug', {}, slugError as Error);
	          }
	        }

	        toast.success('All changes saved successfully!');
	        onSuccess?.();
	        return;
	      }

	      // Check if profile exists
	      if (!profile) {
        // Create new profile
        const { data: userProfile } = await supabase
          .from('users')
          .select('organization_id')
          .eq('id', targetUserId)
          .single();

        if (!userProfile?.organization_id) {
          toast.error('Organization not found');
          return;
        }

        const newProfileData = {
          ...profileData,
          _user_id: targetUserId,
          _organization_id: userProfile.organization_id,
          email: user?.email || '',
          employment_status: 'active'  // Set employment status instead of is_active
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
        const result = await createProfile(newProfileData as any);
        if (result) {
          toast.success('Profile created successfully');
          onSuccess?.();
          // Refresh the profile
          currentProfileHook.refetch();
        }
      } else {
        // Update existing profile
        const result = await updateProfile(targetUserId, profileData);
        if (result) {
          // Verify critical fields were actually saved
          const criticalFields = ['mortgageSpecialties', 'licensedStates', 'nmlsNumber'];
          const missingFields = criticalFields.filter(field => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
            const sentValue = (profileData as any)[field];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- pre-existing legacy lint debt (not part of the OB identity fix)
            const receivedValue = (result as any)[field];
            return sentValue !== undefined && receivedValue === undefined;
          });

          if (missingFields.length > 0) {
            console.warn('⚠️ Some fields may not have been saved:', missingFields);
            toast.warning(`Profile updated but some fields may not have saved: ${missingFields.join(', ')}`);
          }
          // Also update roles in profiles table if admin view
          if (isAdminMode && selectedRoles.length > 0) {
            const formattedRoles = selectedRoles.map(formatRoleForDb);

            const { error: rolesError } = await supabase
              .from('users')
              .update({
                roles: formattedRoles
              })
              .eq('id', targetUserId);

            if (rolesError) {
              log.error('Failed to update roles', { userId: targetUserId }, rolesError);
              toast.error('Failed to update user roles');
            }

            const normalizedEmploymentStatus = employmentStatus.toLowerCase() as 'active' | 'pending' | 'terminated';
            if (normalizedEmploymentStatus !== profile.employmentStatus) {
              if (isOwnProfile) {
                toast.warning(
                  'Your profile was saved without changing employment status. Another administrator must change your employment status.'
                );
              } else {
                try {
                  const statusResult = await setUserEmploymentStatusWithEncompass(
                    targetUserId,
                    normalizedEmploymentStatus
                  );

                  if (statusResult.warning) {
                    toast.warning(`Employment status updated, but Encompass access was not updated: ${statusResult.warning}`);
                  }
                } catch (employmentSyncError) {
                  const message = employmentSyncError instanceof Error
                    ? employmentSyncError.message
                    : 'Unknown Encompass error';
                  log.warn(
                    'Employment status sync failed after profile save',
                    {
                      userId: targetUserId,
                      metadata: { employmentStatus: normalizedEmploymentStatus }
                    },
                    employmentSyncError as Error
                  );
                  toast.warning(
                    `Profile saved, but employment status could not be synchronized with Encompass: ${message}`
                  );
                }
              }
            }
          }

          // Auto-generate website slug if template is selected but no slug exists
          const newTemplate = data.websiteTemplate || selectedTemplate;
          const existingSlug = profile?.websiteSlug || result?.websiteSlug;

          if (!marketingControlsLocked && newTemplate && newTemplate !== 'none' && !existingSlug) {
            try {
              const slug = await ProfileSlugService.generateUniqueSlug(
                targetUserId,
                result.firstName || data.firstName || '',
                result.lastName || data.lastName || ''
              );
              await ProfileSlugService.reserveSlug(targetUserId, slug);
              const url = ProfileSlugService.getShareableUrl(slug);
              setWebsiteUrl(url);
              setValue('websiteSlug', slug);
              log.info('Auto-generated website slug on template selection', { slug, template: newTemplate });
              toast.success('Website URL generated automatically!');
            } catch (slugError) {
              log.warn('Failed to auto-generate website slug', {}, slugError as Error);
              // Don't fail the save - slug can be generated manually later
            }
          }

          // Reset form with new data to clear dirty state
          reset({
            ...result,
            roles: result.roles?.map(formatRoleForDisplay) || [],
            status: toUiStatus(result.employmentStatus)
          });
          setBioCleanupNotice(null);

          toast.success('All changes saved successfully!');
          onSuccess?.();
        }
      }
    } catch (error) {
      log.error('Failed to submit profile', { userId: targetProfileUserId }, error as Error);
      toast.error(error instanceof Error ? error.message : 'Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  };

  // Watch form values for dropdown synchronization
  const watchedRoles = watch('roles');
  const watchedStatus = watch('status');
  const watchedSpecialties = watch('mortgageSpecialties');
  const watchedMortgageIndustryStartYear = watch('mortgageIndustryStartYear');
  const watchedBio = watch('bio');
  const watchedBioValue = watchedBio ?? '';
  const bioWordCount = countProfileBioWords(watchedBio);
  const derivedYearsOfExperience = deriveYearsOfExperience(
    watchedMortgageIndustryStartYear
  );

  const focusBioAt = (cursorPosition: number) => {
    window.requestAnimationFrame(() => {
      const textarea = bioTextareaRef.current;
      if (!textarea) {
        return;
      }
      textarea.focus();
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const handleBioChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBioCleanupNotice(null);
    bioRegistration.onChange(event);
  };

  const handleBioPaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('text/plain');
    const repair = repairProfileBioLineBreakArtifacts(pastedText);

    setBioCleanupNotice(null);
    if (repair.repairedBreaks === 0) {
      return;
    }

    event.preventDefault();
    const textarea = event.currentTarget;
    const selectionStart = textarea.selectionStart ?? textarea.value.length;
    const selectionEnd = textarea.selectionEnd ?? selectionStart;
    const rawValue = `${textarea.value.slice(0, selectionStart)}${pastedText}${textarea.value.slice(selectionEnd)}`;
    const cleanedValue = `${textarea.value.slice(0, selectionStart)}${repair.value}${textarea.value.slice(selectionEnd)}`;
    const cursorPosition = selectionStart + repair.value.length;

    setValue('bio', cleanedValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    setBioCleanupNotice({
      cleanedValue,
      rawValue,
      repairedBreaks: repair.repairedBreaks,
      source: 'paste',
      cursorPosition: selectionStart + pastedText.length
    });
    focusBioAt(cursorPosition);
  };

  const handleManualBioCleanup = () => {
    const repair = repairProfileBioLineBreakArtifacts(watchedBioValue);
    setBioCleanupNotice(null);
    if (repair.repairedBreaks === 0) {
      return;
    }

    const cursorPosition = bioTextareaRef.current?.selectionStart ?? repair.value.length;
    setValue('bio', repair.value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    setBioCleanupNotice({
      cleanedValue: repair.value,
      rawValue: watchedBioValue,
      repairedBreaks: repair.repairedBreaks,
      source: 'manual',
      cursorPosition
    });
  };

  const handleUndoBioCleanup = () => {
    if (!bioCleanupNotice || watchedBioValue !== bioCleanupNotice.cleanedValue) {
      setBioCleanupNotice(null);
      return;
    }

    setValue('bio', bioCleanupNotice.rawValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });
    const cursorPosition = bioCleanupNotice.cursorPosition;
    setBioCleanupNotice(null);
    focusBioAt(cursorPosition);
  };

  // Sync state variables when form values change (especially after reset)
  useEffect(() => {
    if (watchedRoles !== undefined && Array.isArray(watchedRoles)) {
      setSelectedRoles(watchedRoles);
    }
  }, [watchedRoles]);

  useEffect(() => {
    if (watchedStatus !== undefined) {
      setEmploymentStatus(watchedStatus as 'Pending' | 'Active' | 'Terminated');
    }
  }, [watchedStatus]);

  useEffect(() => {
    if (watchedSpecialties !== undefined && Array.isArray(watchedSpecialties)) {
      setMortgageSpecialties(watchedSpecialties);
    }
  }, [watchedSpecialties]);

  // Function to reload profile data from database
  const reloadProfileData = async () => {
    if (userId) {
      // Reload specific user profile
      try {
        const { UserService } = await import('@/services');
        const { data } = await UserService.getUser(userId, supabase);
        if (data) {
          setSpecificProfile(data);

          // Reset all state to database values
          const formattedRoles = data.roles?.map(formatRoleForDisplay) || [];
          setSelectedRoles(formattedRoles);
          setValue('roles', formattedRoles, { shouldDirty: false });

          setPrimaryRole(data.roles?.[0] || 'loan_officer');

          const currentStatus = toUiStatus(data.employmentStatus);
          setEmploymentStatus(currentStatus);
          setValue('status', currentStatus, { shouldDirty: false });

          const specialties = data.mortgageSpecialties || [];
          setMortgageSpecialties(specialties);
          setValue('mortgageSpecialties', specialties, { shouldDirty: false });

          // Reset other fields
          setSpecialties(data.specialties || []);
          setLicenseStates(data.licensedStates || []);
          setSelectedTemplate(data.websiteTemplate || null);

          // Reset the form
          reset({
            ...data,
            roles: formattedRoles,
            status: currentStatus
          });
        }
      } catch (error) {
        log.error('Failed to reload profile', { userId }, error as Error);
      }
    } else if (currentProfileHook.profile) {
      // Reload current user profile - get fresh data from database
      try {
        const { UserService } = await import('@/services');
        const { data, error } = await UserService.getCurrentUser(supabase);
        if (error || !data) {
          log.error('Failed to reload current user profile', {}, error);
          toast.error('Failed to reload profile');
          return;
        }

        // Reset all state to database values
        const formattedRoles = data.roles?.map(formatRoleForDisplay) || [];
        setSelectedRoles(formattedRoles);
        setValue('roles', formattedRoles, { shouldDirty: false });

        setPrimaryRole(data.roles?.[0] || 'loan_officer');

        const currentStatus = toUiStatus(data.employmentStatus);
        setEmploymentStatus(currentStatus);
        setValue('status', currentStatus, { shouldDirty: false });

        const specialties = data.mortgageSpecialties || [];
        setMortgageSpecialties(specialties);
        setValue('mortgageSpecialties', specialties, { shouldDirty: false });

        // Reset other fields
        setSpecialties(data.specialties || []);
        setLicenseStates(data.licensedStates || []);
        setSelectedTemplate(data.websiteTemplate || null);

        // Reset the form
        reset({
          ...data,
          roles: formattedRoles,
          status: currentStatus
        });

        // Trigger hook refetch to update the profile state used by signature display
        await currentProfileHook.refetch();
      } catch (error) {
        log.error('Failed to reload current user profile', {}, error as Error);
        toast.error('Failed to reload profile');
      }
    }
  };

  // Comprehensive discard function
  const handleDiscard = async () => {
    // Simply reload the data from the database
    setBioCleanupNotice(null);
    await reloadProfileData();
    toast.success('Changes discarded');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Derived OB compensation summary used by the Compensation tab when the feature flag
  // is on. `profile` is the typed camelCase User from transformUserFromDB; reading the
  // snake_case DB columns directly returns undefined, which produced a regression where
  // the card showed "No OB plan currently assigned" for every user.
  const currentObPlan = {
    scheduleId: profile?.optimalBlueCurrentScheduleId ?? null,
    scheduleName: profile?.optimalBlueCurrentScheduleName ?? null,
    scope: profile?.optimalBlueCurrentScheduleScope ?? null,
    percentage: profile?.optimalBlueCurrentPercentage ?? null,
    maxCap: profile?.optimalBlueCurrentMaxCap ?? null,
    effectiveDate: profile?.optimalBlueCurrentScheduleEffectiveDate ?? null,
    lastSyncedAt: profile?.optimalBlueLastSyncedAt ?? null
  };

  // Format the last-synced timestamp for display. Mirrors the ObSelectablePlansPanel
  // helper (kept inline rather than imported — 9 lines is below the abstraction
  // threshold for a cross-component dependency).
  const formatObTimestamp = (value: string | null): string => {
    if (!value) {
return 'Never synced from OB';
}
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
return value;
}
    return `Last synced from OB: ${date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })}`;
  };

  // Admin-only "Refresh from OB" — calls ob-comp-inspect-user, stores the response
  // in local state, then refetches the profile so the cache fields and freshness
  // timestamp render fresh. Inspect response drives non-cached branch rendering.
  const handleObRefresh = async () => {
    if (!profile?.id) {
return;
}
    setObInspecting(true);
    try {
      // Repair OB identity (business channel + branch) from OB /users first — the
      // compensation inspect below does NOT touch identity. Best-effort: an identity
      // failure must not block the comp refresh.
      try {
        await refreshObIdentity(profile.id);
      } catch (idErr) {
        log.error('OB identity refresh failed', { userId: profile.id }, idErr as Error);
        toast.warning('Could not refresh OB business channel — see logs');
      }
      const result = await inspectObUser(profile.id);
      setObInspectResult(result);
      // Refetch so optimal_blue_last_synced_at + (for synced branches) cache fields
      // render fresh. Mirrors the pattern at line 1413-1416 (admin path) and the
      // useCurrentUser hook's refetch (self path).
      if (userId) {
        const { UserService } = await import('@/services');
        const { data: fresh } = await UserService.getUser(userId, supabase);
        if (fresh) {
setSpecificProfile(fresh);
}
      } else {
        await currentProfileHook.refetch();
      }
      if (result.status === 'synced') {
        toast.success(`OB plan synced (${result.scope ?? ''})`);
      } else if (result.status === 'missing_plan') {
        toast.info('OB has no plan for this user yet');
      } else if (result.reason === 'future_dated_only') {
        toast.warning('OB has future-dated core assignments that are not active yet');
      } else {
        toast.warning(`OB returned ${result.scope ?? 'unexpected'} state — see panel for detail`);
      }
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Refresh failed: ${msg}`);
      log.error('ob-comp-inspect-user failed', { userId: profile.id }, e as Error);
    } finally {
      setObInspecting(false);
    }
  };

  // Admin-only Repair Core Coverage — fires only for the narrow 13/14-on-one-
  // schedule case (the eligibility predicate is enforced both client-side here
  // and re-validated server-side from live OB at submit time). Writes the
  // missing standard-core category to the same schedule, audits via read-back,
  // syncs the cache. Custom assignments untouched.
  const handleObRepair = async () => {
    if (!profile?.id) {
return;
}
    setObRepairing(true);
    try {
      const result = await repairMissingCore(profile.id);
      if (result.status === 'audit_failed') {
        // Prefer OB's own validation error when it gave us one (much more
        // actionable than our generic "didn't match" wording). Most production
        // failures are OB rejecting the request body — surface that verbatim.
        let toastMessage: string;
        const obError = (result as unknown as { putError?: { status?: number; body?: string } }).putError;
        if (obError && obError.status && obError.status >= 400 && obError.body) {
          // OB returns ProblemDetails JSON with a `title` and structured `errors`. Try to extract.
          let summary = obError.body;
          try {
            const parsed = JSON.parse(obError.body) as {
              title?: string;
              errors?: Record<string, string[]>;
            };
            if (parsed.errors && typeof parsed.errors === 'object') {
              const firstKey = Object.keys(parsed.errors)[0];
              const firstMsg = firstKey ? parsed.errors[firstKey]?.[0] : undefined;
              if (firstMsg) {
summary = `${parsed.title ?? 'OB validation error'}: ${firstMsg}`;
}
            } else if (parsed.title) {
              summary = parsed.title;
            }
          } catch {
            // body wasn't JSON — fall back to raw text
            summary = obError.body.slice(0, 200);
          }
          toastMessage = `Repair rejected by OB (${obError.status}): ${summary}`;
        } else {
          const cat = result.missingCategory ?? 'category';
          const expected = result.expectedScheduleId ?? '?';
          const actualFailure = result.failures?.find((f) => f.productCategory === cat);
          const actualPart = actualFailure
            // eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix)
            ? actualFailure.actualScheduleId == null
              ? 'did not appear in OB after the write'
              : `landed on schedule ${actualFailure.actualScheduleId} instead of ${expected}`
            : 'did not match';
          toastMessage = `Repair failed: ${cat} ${actualPart}. Check OB and retry.`;
        }
        toast.error(toastMessage);
        log.warn('repairMissingCore audit_failed', { userId: profile.id, failures: result.failures, putError: obError });
        return;
      }
      toast.success(`${result.missingCategory} assigned to ${result.scheduleName ?? `schedule ${result.scheduleId}`} — ${profile.firstName ?? 'user'} is now on core`);
      // Refetch so the cache fields (scope, scheduleName, etc.) and timestamp
      // render fresh; also re-run inspect so the needs-review panel disappears.
      if (userId) {
        const { UserService } = await import('@/services');
        const { data: fresh } = await UserService.getUser(userId, supabase);
        if (fresh) {
setSpecificProfile(fresh);
}
      } else {
        await currentProfileHook.refetch();
      }
      try {
        const inspectAfter = await inspectObUser(profile.id);
        setObInspectResult(inspectAfter);
      } catch (e) {
        log.warn('post-repair inspect refresh failed', {}, e as Error);
      }
    } catch (e) {
      const msg = e instanceof CompensationServiceError ? e.message : (e as Error).message;
      toast.error(`Repair failed: ${msg}`);
      log.error('repairMissingCore failed', { userId: profile.id }, e as Error);
    } finally {
      setObRepairing(false);
    }
  };

  const publicationDraft: Partial<User> = {
    ...(profile || {}),
    ...watch(),
    licensedStates: licenseStates,
    mortgageSpecialties,
    websiteTemplate: selectedTemplate || watch('websiteTemplate') || profile?.websiteTemplate,
    websiteSlug: watch('websiteSlug') || profile?.websiteSlug,
    applicationLink: watch('applicationLink') || profile?.applicationLink
  };
  const marketingReadiness = {
    gateEnabled: marketingGateEnabled || marketingAuthorizationLoading,
    governedByRecruiting: marketingAuthorization?.governedByRecruiting
      ?? (marketingAuthorizationLoading || Boolean(marketingAuthorizationError)),
    authorized: !marketingAuthorizationLoading
      && !marketingAuthorizationError
      && Boolean(marketingAuthorization?.authorized)
  };

  const previewAddress = addressType === 'custom' ? customAddress : organizationAddress;
  const companyComplianceReady = isCompanyComplianceReady(publicProfileCompany);
  const companyDisclaimers = Array.isArray(publicProfileCompany?.default_disclaimers)
    ? publicProfileCompany.default_disclaimers
    : [];
  const companyLicenses = Array.isArray(publicProfileCompany?.state_licenses)
    ? publicProfileCompany.state_licenses
    : [];
  const previewCompanyInfo = publicProfileCompany ? {
    name: publicProfileCompany.company_name || '',
    legalName: publicProfileCompany.legal_name || '',
    nmls: publicProfileCompany.company_nmls_id || '',
    phone: publicProfileCompany.main_phone || '',
    email: publicProfileCompany.main_email || '',
    address: formatPublicProfileAddress(publicProfileCompany.headquarters_address),
    street: publicProfileCompany.headquarters_address?.street || undefined,
    city: publicProfileCompany.headquarters_address?.city || undefined,
    state: publicProfileCompany.headquarters_address?.state || undefined,
    zip: publicProfileCompany.headquarters_address?.zip || undefined,
    logo: publicProfileCompany.logo_url || undefined,
    website: publicProfileCompany.website || undefined,
    equalHousingLender: publicProfileCompany.equal_housing_lender === true,
    disclaimers: companyDisclaimers.filter((item) => item.trim().length > 0),
    licenses: companyLicenses.flatMap((license) => {
      const state = license.state?.trim() || '';
      const licenseNumber = license.license_number?.trim() || '';
      return state && licenseNumber ? [{ state, licenseNumber }] : [];
    }),
    complianceReady: companyComplianceReady
  } : undefined;
  const previewProfile: Partial<ProfileData> = {
    firstName: publicationDraft.firstName || '',
    lastName: publicationDraft.lastName || '',
    title: publicationDraft.title || '',
    nmls: publicationDraft.nmlsNumber || '',
    stateLicenseNumber: publicationDraft.stateLicenseNumber,
    phone: publicationDraft.phone
      || (publicationDraft.mobileDisplayConsent ? publicationDraft.mobilePhone : '')
      || '',
    email: isPartnerDelegatedMode ? '' : publicationDraft.email || '',
    bio: publicationDraft.bio || '',
    location: formatPublicProfileAddress(previewAddress),
    licenseStates: publicationDraft.licensedStates || [],
    industryStartYear: publicationDraft.mortgageIndustryStartYear ?? undefined,
    socialMedia: {
      facebook: publicationDraft.facebookUrl,
      linkedin: publicationDraft.linkedinUrl,
      instagram: publicationDraft.instagramUrl,
      tiktok: publicationDraft.tiktokUrl,
      website: publicationDraft.website
    },
    facialPhoto: publicationDraft.profilePhotoFacial,
    bodyPhoto: publicationDraft.profilePhotoBody,
    mortgageSpecialties: publicationDraft.mortgageSpecialties || [],
    reviews: [],
    websiteTemplate: publicationDraft.websiteTemplate,
    slug: publicationDraft.websiteSlug,
    canonicalSlug: publicationDraft.websiteSlug,
    applicationLink: publicationDraft.applicationLink,
    calendarLink: publicationDraft.calendarLink,
    isPartner: publicationDraft.roles?.includes('partner'),
    ...(previewCompanyInfo ? { companyInfo: previewCompanyInfo } : {})
  };

  const profileName = [watch('firstName') || profile?.firstName, watch('lastName') || profile?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim() || 'User profile';
  const joinedAt = profile?.createdAt ? new Date(profile.createdAt) : null;
  const joinedDate = joinedAt && !Number.isNaN(joinedAt.getTime())
    ? joinedAt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : null;
  const statusTone = employmentStatus === 'Active'
    ? 'success'
    : employmentStatus === 'Pending'
      ? 'warning'
      : 'neutral';
  const hasLoanOfficerRole = selectedRoles.some(role => formatRoleForDb(role) === 'loan_officer');
  const handleInvalidSubmit = (formErrors: FieldErrors<UserProfileFormValues>) => {
    if (formErrors.yearsOfExperience) {
      toast.error(formErrors.yearsOfExperience.message || 'Years of Experience is invalid');
    } else if (formErrors.mortgageIndustryStartYear) {
      toast.error(formErrors.mortgageIndustryStartYear.message || 'Mortgage Industry Start Year is invalid');
    } else if (formErrors.nmlsNumber) {
      toast.error('NMLS number is required for Loan Officers');
    }
  };

  const updateEmploymentStatus = (value: 'Pending' | 'Active' | 'Terminated') => {
    setEmploymentStatus(value);
    const originalStatus = toUiStatus(profile?.employmentStatus);
    if (value === originalStatus) {
      resetField('status', { defaultValue: originalStatus });
      return;
    }
    setValue('status', value, { shouldDirty: true });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)} className="lw-user-profile-form">
        {!profile && !userId && (
        <div className="mb-4 mx-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            No loan officer profile found. Fill out the form below to create your profile.
          </p>
        </div>
      )}
      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        localStorage.setItem('profileFormActiveTab', value);
      }} className="lw-user-profile__tabs">
        <div className="lw-user-profile__identity-shell">
          <UserProfileIdentityHeader
            photoUrl={watch('profilePhotoFacial') ?? profile?.profilePhotoFacial}
            name={profileName}
            statusLabel={employmentStatus}
            statusTone={statusTone}
            title={watch('title') ?? profile?.title}
            branch={profile?.branchName}
            email={watch('email') ?? profile?.email}
            nmls={watch('nmlsNumber') ?? profile?.nmlsNumber}
            licensedStateCount={licenseStates.length}
            joinedDate={joinedDate}
            joinedDateTime={profile?.createdAt}
            mode={effectiveManagementMode}
            isDirty={hasPendingChanges}
            isSaving={isSaving || updating}
            onDiscard={handleDiscard}
            onPhotoEdit={() => {
              setActiveTab('personal');
              window.requestAnimationFrame(() => {
                document.getElementById('user-profile-photos')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              });
            }}
          />
          <TabsList className="lw-user-profile__tablist">
          <TabsTrigger className="lw-user-profile__tab" value="personal">Personal & Contact</TabsTrigger>
          <TabsTrigger className="lw-user-profile__tab" value="professional">Professional</TabsTrigger>
          <TabsTrigger className="lw-user-profile__tab" value="marketing">Marketing</TabsTrigger>
          <TabsTrigger className="lw-user-profile__tab" value="credentials">Credentials</TabsTrigger>
          {employeeDocumentsMode && (
            <TabsTrigger className="lw-user-profile__tab" value="documents">Documents</TabsTrigger>
          )}
          {(showCompensation || showSelfCompensation) && (
            <TabsTrigger className="lw-user-profile__tab" value="compensation">Compensation</TabsTrigger>
          )}
          </TabsList>
        </div>

        <TabsContent value="personal" className="lw-user-profile__panel">
          <Card className="lw-user-profile__panel-card">
            <CardHeader className="sr-only">
              <h2 className="font-semibold leading-none tracking-tight">Personal &amp; Contact Information</h2>
              <CardDescription>
                Your profile and contact details
              </CardDescription>
            </CardHeader>
            <CardContent className="lw-user-profile__section-grid">
              <section className="lw-user-profile__section">
                <h2>Name &amp; role</h2>
              {/* Personal Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="firstName"
                    {...register('firstName', { required: 'First name is required' })}
                    className={cn(dirtyFields.firstName && "border-orange-400")}
                  />
                  {errors.firstName && (
                    <p className="text-sm text-red-600">{errors.firstName.message}</p>
                  )}
		                  </div>
		                  <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="lastName"
                    {...register('lastName', { required: 'Last name is required' })}
                    className={cn(dirtyFields.lastName && "border-orange-400")}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              {/* Role Selection and Employment Status */}
              {canManageUser && (
                <div className="lw-user-profile__management space-y-4">
                  <p className="lw-user-profile__management-eyebrow">
                    User management — admin &amp; partner only
                  </p>

                  <div className="space-y-2">
                    <Label htmlFor="user-roles">User Roles</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <MultiSelect
                        id="user-roles"
                        ariaLabel="User roles"
                        options={roleOptions}
	                        value={selectedRoles}
	                        onChange={(rolesArray) => {
	                          setSelectedRoles(rolesArray);
	                          if (areRoleSequencesEqual(profile?.roles, rolesArray)) {
	                            resetField('roles', {
	                              defaultValue: profile?.roles?.map(formatRoleForDisplay) || []
	                            });
	                          } else {
	                            setValue('roles', rolesArray, { shouldDirty: true });
	                          }
                          // Update primary role based on selection (priority order)
                          const newPrimaryRole = rolesArray.includes('Admin') ? 'Admin' :
                                            rolesArray.includes('Manager') ? 'Manager' :
                                            rolesArray.includes('Compliance Specialist') ? 'Compliance Specialist' :
                                            rolesArray.includes('Loan Officer') ? 'Loan Officer' :
                                            rolesArray.includes('Loan Officer Assistant') ? 'Loan Officer Assistant' :
                                            rolesArray.includes('Processing') ? 'Processing' :
                                            rolesArray.includes('Underwriter') ? 'Underwriter' :
                                            rolesArray.includes('Closer') ? 'Closer' :
                                            rolesArray.includes('Partner') ? 'Partner' :
                                            rolesArray[0] || 'Loan Officer';
                          setPrimaryRole(newPrimaryRole);
                        }}
                        placeholder="Add role"
                        inlineTriggerLabel="Add role"
                        presentation="inline"
                        maxItems={roleOptions.length}
                      />
                      <p className="lw-user-profile__management-note">
                        {isPartnerDelegatedMode
                          ? '7 of 10 roles — Admin, Partner & Recruiter are reserved for LendWise admins'
                          : 'All 10 roles available'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="employmentStatus">Employment Status</Label>
                      <Select
                        value={employmentStatus}
                        onValueChange={updateEmploymentStatus}
                      >
                        <SelectTrigger id="employmentStatus" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="lw-ds">
                          {isAdminMode && <SelectItem value="Pending">Pending</SelectItem>}
                          <SelectItem value="Active">Active</SelectItem>
                          {employmentStatus === 'Terminated' && (
                            <SelectItem value="Terminated" disabled>Terminated</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      <p className="lw-user-profile__management-note">
                        {isAdminMode
                          ? 'Includes Pending — admin mode only'
                          : '“Pending” hidden — admin-only option'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              </section>

              {/* Contact Information Section */}
              <section className="lw-user-profile__section">
                <h2>Contact information</h2>
                <div className="lw-user-profile__field-grid">

                {isPartnerDelegatedMode && (
                  <div className="lw-user-profile__mode-note lw-user-profile__field--wide">
                    Email is hidden in partner-delegated mode
                  </div>
                )}

	              {!isPartnerDelegatedMode && (
	                <div className="space-y-2 lw-user-profile__field--wide">
	                  <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
	                  <Input
	                    id="email"
	                    type="email"
	                    {...register('email', { required: 'Email is required' })}
	                    className={cn(dirtyFields.email && "border-orange-400")}
	                  />
	                  {errors.email && (
	                    <p className="text-sm text-red-600">{errors.email.message}</p>
	                  )}
	                </div>
	              )}
                {/* Mobile Phone - At least one phone required */}
                <div className="space-y-2">
                  <Label htmlFor="mobilePhone">Mobile Phone <span className="text-muted-foreground text-xs">(At least one phone required)</span></Label>
                  <Input
                    id="mobilePhone"
                    {...register('mobilePhone')}
                    placeholder="(555) 123-4567"
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setValue('mobilePhone', formatted, { shouldDirty: true });
                    }}
                  />
                  {errors.mobilePhone && (
                    <p className="text-sm text-red-600">{errors.mobilePhone.message}</p>
                  )}
                </div>

                {/* Office Phone - Optional */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Office Phone <span className="text-muted-foreground text-xs">(At least one phone required)</span></Label>
                  <Input
                    id="phone"
                    {...register('phone')}
                    placeholder="(555) 123-4567"
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setValue('phone', formatted, { shouldDirty: true });
                    }}
                  />
                  {errors.phone && (
                    <p className="text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>

                {/* Extension */}
                <div className="space-y-2">
                  <Label htmlFor="officeExtension">Extension</Label>
                  <Input
                    id="officeExtension"
                    {...register('officeExtension')}
                    placeholder="1234"
                  />
                </div>
                </div>
                {(isAdminMode || (effectiveManagementMode === 'self' && isOwnProfile)) && profile && (
                  <ConfigureGmailSignature
                    key={targetProfileUserId}
                    targetUserId={targetProfileUserId}
                    isOwnProfile={isOwnProfile}
                    hasUnsavedChanges={hasPendingChanges || isSaving || updating}
                    disabledReason={profile.employmentStatus !== 'active'
                      ? 'Gmail signatures can be configured for active users.'
                      : marketingControlsLocked
                        ? 'NMLS marketing authorization must be verified before configuring the signature.'
                        : undefined}
                  />
                )}
              </section>

              {/* Physical Address */}
              <section className="lw-user-profile__section lw-user-profile__section--wide">
                <div className="lw-user-profile__section-heading">
                  <h2>Physical address *</h2>
                <Select
                  value={addressType}
                  onValueChange={async (value: 'corporate' | 'custom') => {
                    setAddressType(value);
                    if (value === 'corporate') {
                      await fetchCorporateAddress();
                    }
                  }}
                >
                  <SelectTrigger aria-label="Address source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="lw-ds">
                    <SelectItem value="corporate">Corporate Address</SelectItem>
                    <SelectItem value="custom">Custom Address</SelectItem>
                  </SelectContent>
                </Select>
                {addressType === 'corporate' && (
                  <span>Auto-filled from company HQ — read-only</span>
                )}
                </div>

                {/* Address Fields */}
                <div className="lw-user-profile__address-grid">
                    <div className="space-y-2">
                      <Label htmlFor="street">Street Address</Label>
                      <Input
                        id="street"
                        value={addressType === 'corporate' && organizationAddress ? organizationAddress.street : customAddress.street}
                        onChange={(e) => addressType === 'custom' && setCustomAddress({...customAddress, street: e.target.value})}
                        disabled={addressType === 'corporate'}
                        className={addressType === 'corporate' ? 'bg-muted' : ''}
                        placeholder="123 Main St"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="unit">Unit/Suite (Optional)</Label>
                      <Input
                        id="unit"
                        value={addressType === 'corporate' && organizationAddress ? (organizationAddress.suite || '') : customAddress.unit}
                        onChange={(e) => addressType === 'custom' && setCustomAddress({...customAddress, unit: e.target.value})}
                        disabled={addressType === 'corporate'}
                        className={addressType === 'corporate' ? 'bg-muted' : ''}
                        placeholder="Suite 200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={addressType === 'corporate' && organizationAddress ? organizationAddress.city : customAddress.city}
                        onChange={(e) => addressType === 'custom' && setCustomAddress({...customAddress, city: e.target.value})}
                        disabled={addressType === 'corporate'}
                        className={addressType === 'corporate' ? 'bg-muted' : ''}
                        placeholder="San Francisco"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={addressType === 'corporate' && organizationAddress ? organizationAddress.state : customAddress.state}
                        onChange={(e) => addressType === 'custom' && setCustomAddress({...customAddress, state: e.target.value.toUpperCase()})}
                        disabled={addressType === 'corporate'}
                        className={addressType === 'corporate' ? 'bg-muted' : ''}
                        placeholder="CA"
                        maxLength={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="zip">ZIP Code</Label>
                      <Input
                        id="zip"
                        value={addressType === 'corporate' && organizationAddress ? organizationAddress.zip : customAddress.zip}
                        onChange={(e) => addressType === 'custom' && setCustomAddress({...customAddress, zip: e.target.value})}
                        disabled={addressType === 'corporate'}
                        className={addressType === 'corporate' ? 'bg-muted' : ''}
                        placeholder="94102"
                        maxLength={10}
                      />
                    </div>
                </div>
              </section>

              {/* Professional Information */}
              <section className="lw-user-profile__section">
                <h2>Professional information</h2>
                <div className="lw-user-profile__field-grid">

              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  {...register('title')}
                  placeholder="e.g., Senior Loan Officer"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="yearsOfExperience">Years of Experience</Label>
                <Input
                  id="yearsOfExperience"
                  type="number"
                  min={0}
                  step={1}
                  value={derivedYearsOfExperience ?? ''}
                  disabled
                  readOnly
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mortgageIndustryStartYear">Mortgage Industry Start Year</Label>
                <Input
                  id="mortgageIndustryStartYear"
                  type="number"
                  min={1950}
                  max={new Date().getFullYear()}
                  step={1}
                  disabled={isPartnerDelegatedMode}
                  {...register('mortgageIndustryStartYear', {
                    min: {
                      value: 1950,
                      message: 'Mortgage Industry Start Year must be 1950 or later'
                    },
                    max: {
                      value: new Date().getFullYear(),
                      message: `Mortgage Industry Start Year cannot be later than ${new Date().getFullYear()}`
                    },
                    validate: (value) => (
                      value === null
                      || value === undefined
                      || Number.isInteger(value)
                      || 'Mortgage Industry Start Year must be a whole year'
                    ),
                    setValueAs: normalizeUserProfileNumericInput
                  })}
                  placeholder="e.g., 2012"
                />
                {errors.mortgageIndustryStartYear && (
                  <p className="text-sm text-red-600">{errors.mortgageIndustryStartYear.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Staff-confirmed only. Do not infer this date from NMLS records or years of experience.
                </p>
              </div>

              <div className="space-y-2 lw-user-profile__field--wide">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  {...bioRegistration}
                  ref={(element) => {
                    bioRegistration.ref(element);
                    bioTextareaRef.current = element;
                  }}
                  onChange={handleBioChange}
                  onPaste={handleBioPaste}
                  aria-describedby="bio-guidance"
                  placeholder="Tell us about your experience and expertise..."
                  rows={4}
                />
                {bioCleanupNotice && (
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-foreground">
                    <p role="status">
                      Cleaned {bioCleanupNotice.repairedBreaks} line-break artifacts from {bioCleanupNotice.source === 'paste' ? 'your paste' : 'this bio'}
                    </p>
                    <span aria-hidden="true">—</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs font-semibold"
                      onClick={handleUndoBioCleanup}
                    >
                      Undo
                    </Button>
                  </div>
                )}
                <div id="bio-guidance" className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p>Use blank lines to create paragraph breaks on your public page.</p>
                    {/[\r\n]/u.test(watchedBioValue) && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={handleManualBioCleanup}
                      >
                        Fix line breaks
                      </Button>
                    )}
                  </div>
                  <p aria-live="polite">
                    {bioWordCount} words — {PROFILE_BIO_INDEXING_WORD_FLOOR}+ needed for search indexing
                  </p>
                </div>
              </div>
              </div>
              </section>

	              {/* Enhanced Profile Photos — partner-delegated mode persists
	                  through the audited partner RPC instead of a direct users
	                  update */}
	              <section className="lw-user-profile__section" id="user-profile-photos">
	                <h2>Profile photos</h2>
	                <DualPhotoUpload
	                  userId={userId || user?.id || ''}
	                  facialPhoto={dirtyFields.profilePhotoFacial ? watch('profilePhotoFacial') : (watch('profilePhotoFacial') ?? profile?.profilePhotoFacial)}
	                  bodyPhoto={dirtyFields.profilePhotoBody ? watch('profilePhotoBody') : (watch('profilePhotoBody') ?? profile?.profilePhotoBody)}
	                  onFacialChange={(url) => setValue('profilePhotoFacial', url, { shouldDirty: true })}
	                  onBodyChange={(url) => setValue('profilePhotoBody', url, { shouldDirty: true })}
	                  persistVia={isPartnerDelegatedMode ? 'partner_rpc' : 'direct'}
	                />
	              </section>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="professional" className="lw-user-profile__panel">
          <Card className="lw-user-profile__panel-card">
            <CardHeader className="sr-only">
              <h2 className="font-semibold leading-none tracking-tight">Professional Information</h2>
              <CardDescription>
                Licensing and certification details
              </CardDescription>
            </CardHeader>
            <CardContent className="lw-user-profile__section-grid">
              <section className="lw-user-profile__section">
                <h2>Licensing &amp; identifiers</h2>
              <div className="lw-user-profile__field-grid">
              <div className="space-y-2">
                <Label htmlFor="nmlsNumber">
                  NMLS ID
                  {hasLoanOfficerRole && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input
                  id="nmlsNumber"
                  {...register('nmlsNumber', {
                    required: hasLoanOfficerRole ? 'NMLS ID is required for Loan Officers' : false
                  })}
                  aria-required={hasLoanOfficerRole}
                  placeholder="123456"
                />
                {errors.nmlsNumber && (
                  <p className="text-sm text-red-600">{errors.nmlsNumber.message}</p>
                )}
                {hasLoanOfficerRole && !watch('nmlsNumber') && (
                  <p className="text-xs text-yellow-600">
                    NMLS number is legally required for Loan Officers
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="stateLicenseNumber">State License Number</Label>
                <Input
                  id="stateLicenseNumber"
                  {...register('stateLicenseNumber')}
                />
              </div>

              <div className="space-y-2 lw-user-profile__field--wide">
                <Label htmlFor="newLicenseState">Licensed States</Label>
                <div className="lw-user-profile__licensed-states">
                  {licenseStates.map((state) => (
                    <Badge key={state} variant="secondary" className="lw-user-profile__state-chip">
                      {state}
                      <button
                        type="button"
                        className="lw-user-profile__state-remove"
                        aria-label={`Remove ${state} license`}
                        onClick={() => removeLicenseState(state)}
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </Badge>
                  ))}
                  <Input
                    id="newLicenseState"
                    value={newState}
                    onChange={(e) => setNewState(e.target.value.toUpperCase())}
                    placeholder="XX"
                    maxLength={2}
                    className="lw-user-profile__state-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="lw-user-profile__state-add"
                    onClick={addLicenseState}
                  >
                    + Add state
                  </Button>
                </div>
              </div>
              </div>
              </section>

              <section className="lw-user-profile__section">
                <h2>Mortgage specialties</h2>
              <div className="space-y-2">
                <Label htmlFor="mortgage-specialties">Mortgage Specialties</Label>
                <MultiSelect
                  id="mortgage-specialties"
                  ariaLabel="Mortgage specialties"
                  options={[
                    { value: 'Conventional', label: 'Conventional' },
                    { value: 'FHA', label: 'FHA' },
                    { value: 'VA', label: 'VA' },
                    { value: 'Jumbo', label: 'Jumbo' },
                    { value: 'Non-QM', label: 'Non-QM' },
                    { value: 'DSCR', label: 'DSCR' },
                    { value: 'ITIN', label: 'ITIN' },
                    { value: 'Business Purpose', label: 'Business Purpose' },
                    { value: 'USDA', label: 'USDA' },
                    { value: 'First-Time Home Buyer', label: 'First-Time Home Buyer' },
                    { value: 'Reverse Mortgage', label: 'Reverse Mortgage' }
                  ]}
                  value={mortgageSpecialties}
                  onChange={(value) => {
                    setMortgageSpecialties(value);
                    // Notify react-hook-form of the change
                    setValue('mortgageSpecialties', value, { shouldDirty: true });
                  }}
                  placeholder="Add specialty"
                  inlineTriggerLabel={`Add — ${11 - mortgageSpecialties.length} available`}
                  presentation="inline"
                  maxItems={11}
                />
                <p className="text-xs text-muted-foreground">
                  Choose the loan types and borrower categories you specialize in
                </p>
              </div>
              </section>

	              {/* Preferred Settlement Partners */}
	              <section className="lw-user-profile__section lw-user-profile__section--wide space-y-6">
                <div>
                  <h2>Preferred Settlement Partners</h2>
                  <p className="text-sm text-muted-foreground">
                    Select your preferred title and escrow companies
                  </p>
			              </div>

                <div className="lw-user-profile__field-grid">
                {/* Title Company Selection */}
                <div className="space-y-3">
                  <Label htmlFor="preferredTitleCompany">Preferred Title Company</Label>
                  <Select
                    value={selectedTitleCompanyId || '__none__'}
                    onValueChange={(v) => {
                      const newValue = v === '__none__' ? null : v;
                      setSelectedTitleCompanyId(newValue);
                      setValue('preferredTitleCompanyId', newValue, { shouldDirty: true });
                    }}
                    disabled={loadingTitleEscrow}
                  >
                    <SelectTrigger id="preferredTitleCompany">
                      <SelectValue placeholder={loadingTitleEscrow ? "Loading companies..." : "Select title company..."} />
                    </SelectTrigger>
                    <SelectContent className="lw-ds">
                      <SelectItem value="__none__">None</SelectItem>
                      {titleEscrowCompanies
                        .filter((c) => c.companyType === 'title' || c.companyType === 'both')
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <span>{c.name}</span>
                              {c.companyType === 'title' ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">Title</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Both</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  {/* Inline Fee Display for Title Company */}
                  {selectedTitleCompanyId && titleEscrowCompanies.find((c) => c.id === selectedTitleCompanyId) && (
                    <CompanyFeePreview
                      company={titleEscrowCompanies.find((c) => c.id === selectedTitleCompanyId)!}
                      fees={titleFees}
                      isOpen={titleFeesOpen}
                      onToggle={() => setTitleFeesOpen(!titleFeesOpen)}
                      presentation="profile"
                    />
                  )}
                </div>

                {/* Escrow Company Selection */}
                <div className="space-y-3">
                  <Label htmlFor="preferredEscrowCompany">Preferred Escrow Company</Label>
                  <Select
                    value={selectedEscrowCompanyId || '__none__'}
                    onValueChange={(v) => {
                      const newValue = v === '__none__' ? null : v;
                      setSelectedEscrowCompanyId(newValue);
                      setValue('preferredEscrowCompanyId', newValue, { shouldDirty: true });
                    }}
                    disabled={loadingTitleEscrow}
                  >
                    <SelectTrigger id="preferredEscrowCompany">
                      <SelectValue placeholder={loadingTitleEscrow ? "Loading companies..." : "Select escrow company..."} />
                    </SelectTrigger>
                    <SelectContent className="lw-ds">
                      <SelectItem value="__none__">None</SelectItem>
                      {titleEscrowCompanies
                        .filter((c) => c.companyType === 'escrow' || c.companyType === 'both')
                        .map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex items-center gap-2">
                              <span>{c.name}</span>
                              {c.companyType === 'escrow' ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Escrow</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">Both</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>

                  {/* Inline Fee Display for Escrow Company */}
                  {selectedEscrowCompanyId && titleEscrowCompanies.find((c) => c.id === selectedEscrowCompanyId) && (
                    <CompanyFeePreview
                      company={titleEscrowCompanies.find((c) => c.id === selectedEscrowCompanyId)!}
                      fees={escrowFees}
                      isOpen={escrowFeesOpen}
                      onToggle={() => setEscrowFeesOpen(!escrowFeesOpen)}
                      presentation="profile"
                    />
		                  )}
		                </div>
                </div>
		              </section>
	            </CardContent>
	          </Card>
        </TabsContent>

        <TabsContent value="marketing">
          <Card className="lw-user-profile__panel-card">
            <CardHeader className="sr-only">
              <h2 className="font-semibold leading-none tracking-tight">Marketing &amp; Online Presence</h2>
              <CardDescription>
                Your professional online profiles and marketing preferences
              </CardDescription>
            </CardHeader>

            <CardContent className="lw-user-profile__section-grid">
	              {marketingControlsLocked && (
	                <Alert className="lw-user-profile__section lw-user-profile__section--wide border-warning/40 bg-warning/10">
	                  <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />
	                  <AlertDescription id={marketingLockDescriptionId}>
	                    {marketingAuthorizationLoading
	                      ? 'Checking NMLS authorization before enabling public Marketing tools…'
	                      : marketingAuthorizationError
	                        ? 'We could not verify NMLS authorization. Public Marketing tools remain locked for safety. Refresh the profile or contact an administrator.'
	                        : 'You are not authorized to conduct business in LendWise Mortgage\'s name or take a mortgage application in your name until NMLS records the required authorization. You may continue editing your profile and previewing templates, but template selection, public URL and application-link generation, and publication are locked.'}
	                  </AlertDescription>
	                </Alert>
	              )}
	              <section className="lw-user-profile__section lw-user-profile__section--wide">
	                <h2>Publication readiness</h2>
	              <ProfilePublicationPanel
	                profile={publicationDraft}
	                companyCompliance={publicProfileCompany}
	                published={Boolean(watch('websiteEnabled') ?? profile?.websiteEnabled)}
	                updating={publicationUpdating}
	                hasUnsavedChanges={hasPendingChanges}
	                marketingAuthorization={marketingReadiness}
	                lockDescriptionId={marketingControlsLocked ? marketingLockDescriptionId : undefined}
	                onPublicationChange={setProfilePublication}
	              />
	              {publicProfileSlug && !marketingControlsLocked && (
	                <ProfileDeliveryPanel
	                  state={profileDelivery.state}
	                  loading={profileDelivery.loading}
	                  requesting={profileDelivery.requesting}
	                  canRequestBuild={isAdminMode}
	                  onRequestBuild={profileDelivery.requestBuild}
	                />
	              )}
	              {isAdminMode && targetProfileUserId && (
	                <ProfileSeoApprovalPanel
	                  key={targetProfileUserId}
	                  targetUserId={targetProfileUserId}
	                  published={Boolean(watch('websiteEnabled') ?? profile?.websiteEnabled)}
	                  hasUnsavedChanges={hasPendingChanges}
	                />
	              )}
	              </section>

	              {/* Website Template Selection */}
		              <section className="lw-user-profile__section space-y-4">
                <div className="flex items-center gap-2">
	                  <Globe className="h-5 w-5 text-primary" />
	                  <h2>Professional website</h2>
                </div>

                <WebsiteTemplateGallery
                  selectedTemplate={selectedTemplate || watch('websiteTemplate') || null}
                  onSelectTemplate={(templateId) => {
                    if (!ensureMarketingActionAllowed()) {
                      return;
                    }
                    log.info('onSelectTemplate callback received', {
                      metadata: {
                        newTemplateId: templateId,
                        currentSelectedTemplate: selectedTemplate,
                        currentFormValue: watch('websiteTemplate')
                      }
                    });
                    setSelectedTemplate(templateId);
                    setValue('websiteTemplate', templateId, { shouldDirty: true });
                    log.info('Template selection updated', {
                      metadata: {
                        newSelectedTemplate: templateId,
                        formValueAfterSet: watch('websiteTemplate')
                      }
                    });
                  }}
                  userId={userId || user?.id || ''}
                  previewProfile={previewProfile}
                  selectionDisabled={marketingControlsLocked}
                  selectionDescriptionId={marketingLockDescriptionId}
                />

                {/* Website URL Generator */}
                {selectedTemplate && selectedTemplate !== 'none' && (
                  <div className="mt-4 space-y-4 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="generatedWebsiteUrl">Your Website URL</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={generateWebsiteUrl}
                        disabled={generatingUrl || marketingControlsLocked}
                        aria-describedby={marketingControlsLocked ? marketingLockDescriptionId : undefined}
                      >
                        {generatingUrl ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Globe className="h-4 w-4 mr-2" />
                        )}
                        Generate URL
                      </Button>
                    </div>

                    {websiteUrl && !marketingControlsLocked && (
                      <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                        <Input
                          id="generatedWebsiteUrl"
                          // Never present the permanent per-officer URL before
                          // the published site acknowledges it.
                          value={profileDelivery.state?.shareUrl || websiteUrl}
                          readOnly
                          className="flex-1 bg-transparent border-0"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Copy website URL"
                          onClick={copyWebsiteUrl}
                        >
                          {urlCopied ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label="Open website preview"
                          onClick={() => window.open(profileDelivery.state?.shareUrl || websiteUrl, '_blank')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
	              </section>

	              {/* Application Link Section */}
		              <section className="lw-user-profile__section space-y-4">
                <div className="flex items-center gap-2">
	                  <LinkIcon className="h-5 w-5 text-primary" />
	                  <h2>Application link</h2>
	              </div>

	                <div className="space-y-4">
                  {/* Encompass Sync Status Display */}
                  <div className="space-y-2">
                    <Label>Encompass Integration Status</Label>
                    {profile?.encompassUserId ? (
                      <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-green-700">
                          Synced (ID: <span className="font-mono">{profile.encompassUserId}</span>)
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm text-yellow-700">
                          Not synced - Link Encompass account first
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Generate Button */}
                  <div className="space-y-2">
	                    <Button
	                      type="button"
	                      variant="outline"
	                      onClick={generateApplicationLink}
	                      disabled={!profile?.encompassUserId || generatingApplicationLink || marketingControlsLocked}
	                      aria-describedby={marketingControlsLocked ? marketingLockDescriptionId : undefined}
	                      className="w-full sm:w-auto"
	                    >
	                      {generatingApplicationLink ? (
	                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
	                      ) : (
	                        <RefreshCcw className="w-4 h-4 mr-2" />
	                      )}
	                      Generate Application Link
	                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Your Encompass User ID is automatically used from your synced Encompass account
                    </p>
                  </div>

                  {/* Fields are registered via useEffect, no hidden inputs needed */}

                  {/* Display Generated Link */}
                  {watch('applicationLink') && !marketingControlsLocked && (
                    <div className="space-y-2">
                      <Label htmlFor="generated-application-link">Application Link</Label>
                      <div className="flex gap-2">
                        <Input
                          id="generated-application-link"
                          value={watch('applicationLink') || ''}
                          readOnly
                          className="flex-1 font-mono text-sm bg-muted"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            const link = watch('applicationLink');
                            if (link) {
                              navigator.clipboard.writeText(link);
                              toast.success('Application link copied to clipboard');
                            }
                          }}
                          title="Copy application link"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      {watch('applicationLinkGeneratedAt') && (
                        <p className="text-xs text-muted-foreground">
                          Last generated: {new Date(watch('applicationLinkGeneratedAt')).toLocaleString()}
                        </p>
                      )}
                    </div>
	                  )}
	                </div>
	              </section>

	              <section className="lw-user-profile__section lw-user-profile__section--wide">
	                <h2>Web &amp; social</h2>
	                <div className="lw-user-profile__field-grid lw-user-profile__field-grid--social">
	              <div className="space-y-2">
                <Label htmlFor="website">External Website (Optional)</Label>
                <Input
                  id="website"
                  type="url"
                  {...register('website')}
                  placeholder="https://www.example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="linkedinUrl">LinkedIn Profile</Label>
                <Input
                  id="linkedinUrl"
                  type="url"
                  {...register('linkedinUrl')}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="facebookUrl">Facebook Profile</Label>
                <Input
                  id="facebookUrl"
                  type="url"
                  {...register('facebookUrl')}
                  placeholder="https://facebook.com/yourprofile"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="instagramUrl">Instagram Profile</Label>
                <Input
                  id="instagramUrl"
                  type="url"
                  {...register('instagramUrl')}
                  placeholder="https://instagram.com/yourprofile"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tiktokUrl">TikTok Profile</Label>
                <Input
                  id="tiktokUrl"
                  type="url"
                  {...register('tiktokUrl')}
                  placeholder="https://tiktok.com/@yourprofile"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="calendarLink">Scheduling Calendar Link</Label>
                <Input
                  id="calendarLink"
                  type="url"
                  {...register('calendarLink')}
                  placeholder="https://calendly.com/yourprofile"
                />
              </div>
	                </div>
	              </section>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="credentials">
          <Card className="lw-user-profile__panel-card">
            <CardHeader className="sr-only">
              <h2 className="font-semibold leading-none tracking-tight">Credentials</h2>
              <CardDescription>
                Professional credentials and third-party service integrations
              </CardDescription>
            </CardHeader>
            <CardContent className="lw-user-profile__credentials-grid">
              {!isAdminMode && !isOwnProfile && (
                <div className="lw-user-profile__mode-note">
                  {isPartnerDelegatedMode
                    ? 'Partner-delegated mode: Encompass/OB credential cards, SMS & voice forwarding, signature and Xactus are hidden — managed by LendWise admins.'
                    : 'Encompass & Optimal Blue connections are managed by your admin — they don’t appear here.'}
                </div>
              )}

              <ProfileCredentialIntegrations
                actorUserId={user?.id}
                targetUserId={targetProfileUserId}
                userEmail={profile?.email || ''}
                firstName={profile?.firstName}
                lastName={profile?.lastName}
                managementMode={effectiveManagementMode}
                isAdminView={isAdminMode}
                presentation="profile"
              />

              <div className="lw-user-profile__credential-pair grid grid-cols-1 items-start gap-4 md:grid-cols-2">
                {/* Twilio SMS Integration */}
                <TwilioSmsCredentials
                  userId={userId || user?.id || ''}
                  userEmail={profile?.email || ''}
                  firstName={profile?.firstName}
                  lastName={profile?.lastName}
                  isAdminView={isAdminMode}
                  canManageNumbers={canManageUser}
                  canSync={isAdminMode}
                  targetUserId={userId || user?.id || ''}
                  presentation="profile"
                />

                {/* SMS & Voice Forwarding Settings */}
                {!isPartnerDelegatedMode && (
                  <div className="lw-credential-card lw-credential-card--forwarding border rounded-lg p-6 space-y-4">
                    <div>
                      <h3 className="lw-credential-card__title text-lg font-semibold">SMS &amp; voice forwarding</h3>
                      <p className="lw-credential-card__note">
                        Forward borrower messages and calls to the mobile number on this profile.
                      </p>
                    </div>

                {/* SMS Forwarding */}
                <div className="lw-credential-card__setting-group">
                  <div className="lw-credential-card__setting-row flex items-center justify-between">
                    <div className="min-w-0 space-y-0.5">
                      <Label htmlFor="sms-forwarding-enabled">Forward SMS to Mobile</Label>
                      <p className="lw-credential-card__caption">
                        Borrower texts notify
                        {watch('mobilePhone') && ` ${formatPhoneNumber(watch('mobilePhone'))}`}
                      </p>
                    </div>
                    <Switch
                      id="sms-forwarding-enabled"
                      checked={watch('smsForwardingEnabled') ?? true}
                      onCheckedChange={(checked) =>
                        setValue('smsForwardingEnabled', checked, { shouldDirty: true })
                      }
                      disabled={!watch('mobilePhone')}
                    />
                  </div>

                  {watch('smsForwardingEnabled') && watch('mobilePhone') && (
                    <div className="lw-credential-card__format">
                      <Label>Message format</Label>
                      <RadioGroup
                        value={watch('smsForwardingFormat') || 'rich'}
                        onValueChange={(value) =>
                          setValue('smsForwardingFormat', value, { shouldDirty: true })
                        }
                        className="lw-credential-card__format-options"
                      >
                        <div className="lw-credential-card__format-option flex items-center space-x-2">
                          <RadioGroupItem value="rich" id="rich" />
                          <Label htmlFor="rich" className="font-normal cursor-pointer">
                            Rich <span>— sender + context</span>
                          </Label>
                        </div>
                        <div className="lw-credential-card__format-option flex items-center space-x-2">
                          <RadioGroupItem value="simple" id="simple" />
                          <Label htmlFor="simple" className="font-normal cursor-pointer">
                            Simple <span>— message only</span>
                          </Label>
                        </div>
                      </RadioGroup>

                      <p className="lw-credential-card__caption">
                        Forwarded messages are notifications only; reply to borrowers in LendWise.
                      </p>
                    </div>
                  )}
                </div>

                {/* Voice Call Forwarding - full workflow opens from the compact profile entry point. */}
                {profile && (
                  <div className="lw-credential-card__setting-row flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span
                      className="min-w-0 text-sm text-[var(--ink-soft)]"
                      title={profile.voiceGreetingText || undefined}
                    >
                      Voice greeting — {profile.elevenlabsVoiceId || profile.voiceGreetingAudioUrl
                        ? 'Configured'
                        : 'Not configured'}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      <audio
                        ref={voicePreviewRef}
                        src={profile.voiceGreetingAudioUrl || undefined}
                        className="hidden"
                        preload="none"
                        onPlay={() => setVoicePreviewPlaying(true)}
                        onPause={() => setVoicePreviewPlaying(false)}
                        onEnded={() => setVoicePreviewPlaying(false)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="lw-credential-card__compact-action"
                        disabled={!profile.voiceGreetingAudioUrl}
                        onClick={() => {
                          const audio = voicePreviewRef.current;
                          if (!audio) {
                            return;
                          }
                          if (audio.paused) {
                            void audio.play().catch(() => {
                              setVoicePreviewPlaying(false);
                              toast.error('Unable to play the saved voice greeting');
                            });
                          } else {
                            audio.pause();
                          }
                        }}
                      >
                        {voicePreviewPlaying ? (
                          <Pause className="mr-1.5 h-3.5 w-3.5" />
                        ) : (
                          <Play className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        {voicePreviewPlaying ? 'Pause' : 'Preview'}
                      </Button>
                      <Button
                        ref={voiceStudioTriggerRef}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="lw-credential-card__compact-action"
                        onClick={() => {
                          if (voicePreviewPlaying) {
                            voicePreviewRef.current?.pause();
                          }
                          setVoiceStudioOpen(true);
                        }}
                      >
                        Voice Studio…
                      </Button>
                    </div>
                  </div>
                )}

                {/* Warning if no mobile phone */}
                {!watch('mobilePhone') && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Add your mobile phone number in the Contact Information section to enable SMS and voice forwarding.
                    </AlertDescription>
                  </Alert>
                )}
                  </div>
                )}
              </div>

              {!isPartnerDelegatedMode && (
                <>
              <div className="lw-user-profile__credential-pair grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Default Signature for Wisr SMS Pre-Approvals */}
              <div className="lw-credential-card lw-credential-card--signature border rounded-lg p-6 space-y-4">
                <div>
                  <h3 className="lw-credential-card__title text-lg font-semibold">Default signature</h3>
                </div>
                <p className="lw-credential-card__note">
                  Used for automatic e-signing on Wisr SMS pre-approval letters.
                </p>

                {profile?.defaultSignatureDataUrl ? (
                  // Show saved signature preview
                  <div className="space-y-3">
                    <div className="lw-credential-card__signature-well">
                      <img
                        src={profile.defaultSignatureDataUrl}
                        alt="Saved signature"
                        className="max-h-[120px] mx-auto"
                      />
                    </div>
                    <div className="lw-credential-card__signature-meta">
                      <div className="lw-credential-card__caption">
                        {profile.defaultSignatureMethod === 'typed' ? (
                          <span>Typed signature using {profile.defaultSignatureFont}</span>
                        ) : (
                          <span>Drawn signature</span>
                        )}
                        {profile.defaultSignatureSavedAt && (
                          <span className="ml-2">
                            · Saved {new Date(profile.defaultSignatureSavedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="lw-credential-card__actions flex flex-wrap items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowSignatureCapture(true)}
                          disabled={savingSignature}
                        >
                          Change — draw or type
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveDefaultSignature}
                          disabled={savingSignature}
                          className="lw-credential-card__action--danger"
                        >
                          {savingSignature ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : showSignatureCapture ? (
                  // Show signature capture UI
                  <div className="space-y-4">
                    <SignatureCapture
                      methods={['drawn', 'typed']}
                      defaultMethod="typed"
                      initialName={profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : ''}
                      width={700}
                      height={180}
                      onSignatureComplete={handleSaveDefaultSignature}
                      onClear={() => {}}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowSignatureCapture(false)}
                      className="w-full"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  // Show button to create signature
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="lw-credential-card__compact-action"
                    onClick={() => setShowSignatureCapture(true)}
                  >
                    Create default signature
                  </Button>
                )}
              </div>

              {/* Xactus Credentials Request */}
              <div className="lw-credential-card lw-credential-card--xactus border rounded-lg p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <h3 className="lw-credential-card__title text-lg font-semibold">
                      Xactus verification
                    </h3>
                    <p className="lw-credential-card__note">
                      Credit &amp; verification bundle for pre-approvals.
                    </p>
                  </div>
                </div>

                <div className="lw-credential-card__xactus-action">
                  <Button
                    type="button"
                    onClick={handleRequestXactusCredentials}
                    className="lw-credential-card__primary-action"
                    disabled={xactusSubmitting}
                  >
                    {xactusSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Request Xactus credentials'
                    )}
                  </Button>

                  <p className={cn(
                    'lw-credential-card__caption lw-credential-card__nmls-caption',
                    profile?.nmlsNumber && 'lw-credential-card__nmls-caption--ready'
                  )}>
                    {profile?.nmlsNumber
                      ? `Requires an NMLS ID on file — ${profile.nmlsNumber} ✓`
                      : 'Requires an NMLS ID on file'}
                  </p>
                </div>
              </div>
		              </div>

              {/* Request History Section */}
		              <div className="lw-credential-card lw-credential-card--history border rounded-lg p-6 space-y-4">
                <h3 className="lw-credential-card__title text-lg font-semibold">Recent credential requests</h3>

                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : xactusHistory.length === 0 ? (
                  <div className="text-center py-8 text-sm text-muted-foreground">
                    No credential requests have been made yet.
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table
                      containerProps={{
                        tabIndex: 0,
                        'aria-label': 'Credential request history table'
                      }}
                    >
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>NMLS</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Date & Time</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {xactusHistory.map((request) => (
                          <TableRow key={request.id}>
                            <TableCell className="font-medium">
                              {request.first_name} {request.last_name}
                              {request.id === user?.id && (
                                <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                              )}
                            </TableCell>
                            <TableCell>{request.nmls_number || '—'}</TableCell>
                            <TableCell className="text-sm">{request.email}</TableCell>
                            <TableCell className="text-sm">
                              {new Date(request.xactus_request_date).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </TableCell>
                            <TableCell>
                              {request.xactus_request_status === 'success' ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100">
                                  Success
                                </Badge>
                              ) : request.xactus_request_status === 'manual_review' ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100">
                                  Manual Review
                                </Badge>
                              ) : (
                                <Badge variant="outline">Unknown</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
		              </div>
		                </>
		              )}
            </CardContent>
          </Card>
        </TabsContent>

        {employeeDocumentsMode && targetProfileUserId && (
          <TabsContent value="documents" className="lw-user-profile__panel">
            <React.Suspense
              fallback={(
                <div className="flex min-h-40 items-center justify-center" role="status" aria-live="polite">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                  Loading employee documents…
                </div>
              )}
            >
              <EmployeeDocumentsTab
                key={`${targetProfileUserId}:${employeeDocumentsMode}`}
                targetUserId={targetProfileUserId}
                mode={employeeDocumentsMode}
              />
            </React.Suspense>
          </TabsContent>
        )}

        {/* Compensation Tab (Admin-only for Loan Officers, or self-view read-only) */}
        {showCompensation && (
          <TabsContent value="compensation" className="lw-user-profile__panel lw-user-profile__compensation">
            {obCompFeatureEnabled ? (
              /* OB-driven compensation flow (plan v5). Reads from users.optimal_blue_current_*
                 and compensation_change_requests; submits via compRequest. */
              <Card className="lw-user-profile__panel-card">
                <CardHeader>
                  <h2 className="font-semibold leading-none tracking-tight">Compensation Settings</h2>
                  <CardDescription>
                    Your compensation plan is set in Optimal Blue. Changes are scheduled for the 1st of next month and applied automatically by the system.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* OB current plan summary. Branch order:
                      1. Inspect needs_review — live OB does not reduce to a clean active-now
                         canonical state. Render from the inspect response, not the cache,
                         because ob_comp_assignments is append-only with no tombstone.
                      2. Inspect missing_plan OR (no inspect AND no cached scheduleId) — empty
                         state. Admin sees Setup CTA; LO sees "contact admin" copy.
                      3. Cached scheduleId set — render the canonical cache (synced state).
                  */}
                  <div className="rounded border p-4 bg-muted/30">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Current OB plan</div>

                    {obInspectResult?.status === 'needs_review' && obInspectResult.reason === 'future_dated_only' ? (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="font-semibold mb-1">Future-dated OB assignments pending</div>
                          <div className="text-sm">
                            OB has standard core assignments scheduled in the future. Lendwise only treats
                            compensation changes as successful once they are active now in live OB, so this
                            stays in review until those dates pass or an admin fixes it directly in OB.
                          </div>
                          {obInspectResult.upcoming && obInspectResult.upcoming.length > 0 && (
                            <ul className="list-disc pl-5 mt-2 text-xs space-y-0.5">
                              {obInspectResult.upcoming.slice(0, 8).map((u) => (
                                <li key={`${u.productCategory}-${u.scheduleId}-${u.effectiveDate}`}>
                                  <span className="font-mono">{u.productCategory}</span>
                                  {' → '}{u.scheduleName} (effective {u.effectiveDate.slice(0, 10)})
                                </li>
                              ))}
                              {obInspectResult.upcoming.length > 8 && (
                                <li className="italic">…and {obInspectResult.upcoming.length - 8} more</li>
                              )}
                            </ul>
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : obInspectResult?.status === 'needs_review' &&
                       (obInspectResult.reason === 'mixed_core' || obInspectResult.reason === 'mixed_or_custom') ? (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <div className="font-semibold mb-1">
                            Standard core categories incomplete or split — manual review needed
                          </div>
                          <div className="text-sm">
                            One or more of the 14 standard core categories is unassigned in OB, or the standard
                            core is split across multiple schedules. An admin needs to reconcile the standard
                            core in OB before self-service is available. Custom assignments are out of scope
                            for this workflow and don&apos;t need to change.
                          </div>
                          {obInspectResult.details && obInspectResult.details.length > 0 && (
                            <ul className="list-disc pl-5 mt-2 text-xs space-y-0.5">
                              {obInspectResult.details.slice(0, 14).map((d) => (
                                <li key={`${d.productCategory}-${d.scheduleId}`}>
                                  <span className="font-mono">{d.productCategory}</span>
                                  {' → schedule '}<span className="font-medium">{d.scheduleId}</span>
                                </li>
                              ))}
                              {obInspectResult.details.length > 14 && (
                                <li className="italic">…and {obInspectResult.details.length - 14} more</li>
                              )}
                            </ul>
                          )}

                          {/* Repair Core Coverage — admin-only, fires ONLY for the narrow safe case:
                              13 of 14 standard-core categories on a single schedule. Anything else
                              (multi-schedule split, more than one missing) stays in pure manual review.
                              Server re-validates the predicate against live OB so a UI race can't bypass.
                              Re-enabled 2026-04-19 after putObAssignments switched to OB's documented
                              bulk PUT contract. */}
                          {(() => {
                            if (!isAdminView) {
return null;
}
                            const details = obInspectResult.details ?? [];
                            const distinctSchedules = new Set(details.map((d) => d.scheduleId));
                            const eligible =
                              details.length === STANDARD_CORE_PRODUCT_CATEGORIES_CLIENT.length - 1 &&
                              distinctSchedules.size === 1;
                            if (!eligible) {
return null;
}
                            const presentSet = new Set(details.map((d) => d.productCategory));
                            const missing = STANDARD_CORE_PRODUCT_CATEGORIES_CLIENT.find((c) => !presentSet.has(c));
                            const scheduleId = details[0].scheduleId;
                            return (
                              <div className="mt-3 pt-3 border-t border-muted-foreground/20 space-y-2">
                                <div className="text-xs text-muted-foreground">
                                  Will assign <span className="font-mono font-medium">{missing}</span>
                                  {' to schedule '}<span className="font-medium">{scheduleId}</span>
                                  {' in OB. Lendwise only updates if OB confirms it is active now. Custom assignments untouched.'}
                                </div>
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={handleObRepair}
                                  disabled={obRepairing}
                                >
                                  {obRepairing ? (
                                    <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Repairing…</>
                                  ) : (
                                    <>Repair Core Coverage</>
                                  )}
                                </Button>
                              </div>
                            );
                          })()}
                        </AlertDescription>
                      </Alert>
                    ) : obInspectResult?.status === 'missing_plan' || (!obInspectResult && !currentObPlan.scheduleId) ? (
                      isAdminView ? (
                        <div className="space-y-2">
                        <div className="text-sm text-muted-foreground">
                          No OB plan assigned for this user yet. Set up an initial plan across the 14
                            standard core categories. Lendwise only updates after live OB confirms the plan
                            is active now.
                        </div>
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => setObInitialAssignOpen(true)}
                          >
                            Set Up Compensation
                          </Button>
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          No OB plan currently assigned. Contact an admin if this looks wrong.
                        </div>
                      )
                    ) : currentObPlan.scheduleId ? (
                      <>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="font-semibold text-base">
                            {currentObPlan.scheduleName ?? 'Unnamed plan'}
                          </div>
                          <Badge variant="outline">
                            {currentObPlan.scope ?? 'unknown scope'}
                          </Badge>
                          <div className="text-sm text-muted-foreground">
                            {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                            {currentObPlan.percentage != null ? `${currentObPlan.percentage}%` : '—'}
                            {' · cap '}
                            {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                            {currentObPlan.maxCap == null || currentObPlan.maxCap === 0
                              ? 'No cap'
                              : `$${Number(currentObPlan.maxCap).toLocaleString()}`}
                          </div>
                        </div>
                        {currentObPlan.effectiveDate && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Effective {currentObPlan.effectiveDate}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">
                        No OB plan currently assigned. Contact an admin if this looks wrong.
                      </div>
                    )}

                    {/* Admin-only footer: freshness indicator + Refresh from OB. Always visible
                        to admins regardless of branch above so they can re-run inspect. */}
                    {isAdminView && (
                      <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3">
                        <div className="text-xs text-muted-foreground">
                          {formatObTimestamp(currentObPlan.lastSyncedAt)}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleObRefresh}
                          disabled={obInspecting}
                        >
                          {obInspecting ? (
                            <><Loader2 className="mr-2 h-3 w-3 animate-spin" /> Refreshing…</>
                          ) : (
                            <><RefreshCw className="mr-2 h-3 w-3" /> Refresh from OB</>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Open request banner (from compensation_change_requests, NOT users.pending_*) */}
                  {obOpenRequest && (
                    <Alert className={
                      obOpenRequest.status === 'needs_review' || obOpenRequest.status === 'failed'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }>
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Open request: </strong>
                        {obOpenRequest.target_schedule_name}
                        {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                        {' '}({obOpenRequest.target_percentage != null ? `${obOpenRequest.target_percentage}%` : '—'},{' '}
                        {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                        cap {obOpenRequest.target_max_cap == null || obOpenRequest.target_max_cap === 0
                          ? 'No cap'
                          : `$${Number(obOpenRequest.target_max_cap).toLocaleString()}`}),{' '}
                        effective {obOpenRequest.effective_date}.{' '}
                        Status: <strong>{formatCompRequestStatus(obOpenRequest.status, obOpenRequest.effective_date, obOpenRequest.last_failure_reason)}</strong>.
                        {obOpenRequest.last_failure_reason && (
                          <div className="text-sm mt-1">Reason: {obOpenRequest.last_failure_reason}</div>
                        )}
                        <br />
                        <span className="text-sm text-muted-foreground">
                          You cannot submit a new request until this one is resolved.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Change Plan button — disabled when an open request exists or scope is ineligible.
                      type="button" is critical: this lives inside the parent profile <form>, so without
                      it the button defaults to type="submit" and clicking it would submit the profile
                      form (firing "Profile updated successfully" + page refresh) instead of opening
                      the dialog. */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={() => setObChangePlanDialogOpen(true)}
                      disabled={
                        !!obOpenRequest ||
                        // Core-only gate. Server (comp-request) refuses anything except scope='core',
                        // so we mirror that here. Pre-rollout users with stale cached scope='full_footprint'
                        // or 'custom_only' are intentionally blocked until they refresh — clicking
                        // Refresh from OB re-derives them to 'core' (or 'mixed'/'none') and the
                        // button re-enables when appropriate. Avoids the click-then-server-rejected drift.
                        currentObPlan.scope !== 'core' ||
                        // When a live inspect has diagnosed a non-canonical state, block the
                        // self-serve path even if the cached scope still says core. The
                        // needs_review / missing_plan branches render their own panels above
                        // and the Change Plan flow would drift-fail server-side anyway.
                        obInspectResult?.status === 'missing_plan' ||
                        obInspectResult?.status === 'needs_review'
                      }
                    >
                      {obOpenRequest ? formatCompRequestStatus(obOpenRequest.status, obOpenRequest.effective_date, obOpenRequest.last_failure_reason) : 'Change Plan'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
            <Card className="lw-user-profile__panel-card">
              <CardHeader>
                <h2 className="font-semibold leading-none tracking-tight">Compensation Settings</h2>
                <CardDescription>
                  View your current compensation. Request changes below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Pending Request Banner */}
                {pendingCompensationRequest && (
                  <Alert className="bg-yellow-50 border-yellow-200">
                    <AlertCircle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription>
                      <strong>Pending Request:</strong> You have a compensation change request
                      submitted on {new Date(pendingCompensationRequest.compensation_request_submitted_at!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })},
                      effective {parseAsLocalDate(pendingCompensationRequest.compensation_request_effective_date!).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                      <br />
                      <span className="text-sm text-muted-foreground">
                        You cannot submit a new request until this one is resolved.
                      </span>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Compensation Amounts Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Compensation Amounts</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    {/* Banked Compensation */}
                    <div className="space-y-2">
                      <Label htmlFor="banked_compensation">Banked Compensation (%)</Label>
                      <Select
                        value={compensationData.banked_compensation?.toFixed(2) || ''}
                        onValueChange={(value) => setCompensationData(prev => ({
                          ...prev,
                          banked_compensation: value ? parseFloat(value) : null
                        }))}
                        disabled
                      >
                        <SelectTrigger id="banked_compensation">
                          <SelectValue placeholder="Select percentage" />
                        </SelectTrigger>
                        <SelectContent className="lw-ds">
                          {BANKED_COMPENSATION_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">Range: 0.75% - 3.00%</p>
                    </div>

                    {/* Broker Commission Split */}
                    <div className="space-y-2">
                      <Label htmlFor="broker_commission_split">Broker Commission Split (%)</Label>
                      <Input
                        id="broker_commission_split"
                        type="number"
                        min={0}
                        max={85}
                        value={compensationData.broker_commission_split || ''}
                        onChange={(e) => setCompensationData(prev => ({
                          ...prev,
                          broker_commission_split: e.target.value ? parseFloat(e.target.value) : null
                        }))}
                        disabled
                      />
                      <p className="text-xs text-muted-foreground">Range: 0% - 85%</p>
                    </div>

                    {/* Maximum Compensation */}
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="maximum-compensation">Maximum Compensation</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                        <Input
                          id="maximum-compensation"
                          type="text"
                          className="pl-8"
                          value={compensationData.maximum_compensation?.toLocaleString() || ''}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setCompensationData(prev => ({
                              ...prev,
                              maximum_compensation: value ? parseInt(value) : null
                            }));
                          }}
                          disabled
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Per-deal cap ($10,000 - $999,999)</p>
                    </div>
                  </div>
                </div>

                {/* Mentorship Status (read-only display) */}
                {compensationData.mentorship_active && (
                  <div className="p-4 bg-muted rounded-lg">
                    <p className="text-sm">
                      <strong>Mentorship Active:</strong> {compensationData.brokered_deals_completed}/5 deals completed
                    </p>
                  </div>
                )}

                {/* Request Change Button */}
                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={() => setRequestModalOpen(true)}
                    disabled={!!pendingCompensationRequest}
                  >
                    {pendingCompensationRequest ? 'Request Pending' : 'Request Change'}
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </TabsContent>
        )}

        {/* Self-serve compensation tab. Renders only on /my-profile (or any
            mount that passes showSelfCompensation=true). No admin tooling —
            see admin tab above for Refresh/Setup/Repair/Inspect. */}
        {showSelfCompensation && profile && (
          <TabsContent value="compensation" className="lw-user-profile__panel lw-user-profile__compensation">
            {!obCompFeatureEnabled ? (
              <Card className="lw-user-profile__panel-card">
                <CardContent className="py-8">
                  <p className="text-sm text-muted-foreground">
                    Self-serve compensation is not enabled for your account yet.
                  </p>
                </CardContent>
              </Card>
            ) : !profile.optimalBlueUserId ? (
              <Card className="lw-user-profile__panel-card">
                <CardHeader>
                  <h2 className="font-semibold leading-none tracking-tight">My Compensation</h2>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Your account is not linked to Optimal Blue. Contact an admin to link your OB account.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="lw-user-profile__compensation-stack space-y-4">
                <Card className="lw-user-profile__compensation-card">
                  <CardHeader>
                    <h2 className="font-semibold leading-none tracking-tight">Current Compensation</h2>
                    <CardDescription>
                      Your compensation plan is set in Optimal Blue. Changes are scheduled for the 1st of next month and applied automatically by the system.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {currentObPlan.scope === 'core' && currentObPlan.scheduleName ? (
                      <div className="flex flex-col gap-2">
                        <div className="text-sm">
                          Plan: <span className="font-semibold">{currentObPlan.scheduleName}</span>
                          {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                          {currentObPlan.percentage != null && (
                            <span className="text-muted-foreground"> · {currentObPlan.percentage}%</span>
                          )}
                          {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                          {currentObPlan.maxCap != null && currentObPlan.maxCap !== 0 && (
                            <span className="text-muted-foreground"> · cap ${Number(currentObPlan.maxCap).toLocaleString()}</span>
                          )}
                        </div>
                        {currentObPlan.lastSyncedAt && (
                          <div className="text-xs text-muted-foreground">
                            Last synced {new Date(currentObPlan.lastSyncedAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          Your OB plan needs a refresh before you can request a change. Contact an admin to refresh your plan from Optimal Blue.
                        </AlertDescription>
                      </Alert>
                    )}

                    {obOpenRequest && (
                      <Alert variant={obOpenRequest.status === 'needs_review' || obOpenRequest.status === 'failed' ? 'destructive' : 'default'}>
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          {obOpenRequest.target_schedule_name}
                          {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                          {' '}({obOpenRequest.target_percentage != null ? `${obOpenRequest.target_percentage}%` : '—'},{' '}
                          {/* eslint-disable-next-line eqeqeq -- pre-existing legacy lint debt (not part of the OB identity fix) */}
                          cap {obOpenRequest.target_max_cap == null || obOpenRequest.target_max_cap === 0
                            ? 'no cap'
                            : `$${Number(obOpenRequest.target_max_cap).toLocaleString()}`}),{' '}
                          effective {obOpenRequest.effective_date}.{' '}
                          Status: <strong>{formatCompRequestStatus(obOpenRequest.status, obOpenRequest.effective_date, obOpenRequest.last_failure_reason)}</strong>.
                          {obOpenRequest.last_failure_reason && (
                            <div className="text-sm mt-1">Reason: {obOpenRequest.last_failure_reason}</div>
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={() => setObChangePlanDialogOpen(true)}
                        disabled={!!obOpenRequest || currentObPlan.scope !== 'core'}
                      >
                        {obOpenRequest ? formatCompRequestStatus(obOpenRequest.status, obOpenRequest.effective_date, obOpenRequest.last_failure_reason) : 'Change Plan'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <ObCompRequestsPanel
                  mode="self"
                  userId={profile.id}
                  onChange={async () => {
                    // Self-cancel (or any panel mutation) refetches the profile
                    // here; the widened line-564 effect re-fires and clears the
                    // stale obOpenRequest banner / Change Plan disabled state.
                    if (userId) {
                      const { UserService } = await import('@/services');
                      const { data: fresh } = await UserService.getUser(userId, supabase);
                      if (fresh) {
setSpecificProfile(fresh);
}
                    } else {
                      await currentProfileHook.refetch();
                    }
                  }}
                />
              </div>
            )}
          </TabsContent>
        )}

      </Tabs>

      {profile && (
        <Dialog open={voiceStudioOpen} onOpenChange={setVoiceStudioOpen}>
          <DialogContent
            className="max-h-[95dvh] w-[calc(100vw-1.5rem)] max-w-6xl overflow-y-auto p-4 sm:p-6"
            onCloseAutoFocus={(event) => {
              const trigger = voiceStudioTriggerRef.current;
              if (trigger?.isConnected && !trigger.disabled) {
                event.preventDefault();
                trigger.focus();
              }
            }}
          >
            <DialogHeader className="sr-only">
              <DialogTitle>Voice Studio</DialogTitle>
              <DialogDescription>
                Preview and configure the AI voice greeting for this user.
              </DialogDescription>
            </DialogHeader>
            <VoiceStudio
              userId={profile.id}
              userProfile={{
                gender: profile.gender,
                firstName: profile.firstName,
                lastName: profile.lastName
              }}
              currentVoiceId={profile.elevenlabsVoiceId}
              currentAudioUrl={profile.voiceGreetingAudioUrl}
              currentGreeting={profile.voiceGreetingText}
              onVoiceConfigured={async () => {
                await reloadProfileData();
                toast.success('Voice settings updated successfully');
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Xactus Submission Modal */}
      <Dialog open={xactusModalOpen} onOpenChange={setXactusModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {xactusStatus === 'preparing' && 'Preparing Xactus Form...'}
              {xactusStatus === 'submitting' && 'Submitting to Xactus...'}
              {xactusStatus === 'success' && 'Success!'}
              {xactusStatus === 'manual' && 'Review and Submit'}
              {xactusStatus === 'error' && 'Submission Error'}
            </DialogTitle>
            <DialogDescription>
              {xactusStatus === 'preparing' && 'Gathering your information...'}
              {xactusStatus === 'submitting' && 'Attempting direct submission...'}
              {xactusStatus === 'success' && 'Confirmed! Your request was successfully submitted to Xactus.'}
              {xactusStatus === 'manual' && 'Please review the pre-filled form and click Submit. We\'ll automatically detect when it\'s submitted.'}
              {xactusStatus === 'error' && 'There was an error submitting the form. Please try again.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {xactusSubmitting && xactusStatus !== 'manual' && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">
                  {xactusStatus === 'preparing' && 'Loading company and profile information...'}
                  {xactusStatus === 'submitting' && 'Submitting form data to Xactus...'}
                </p>
              </div>
            )}

            {xactusStatus === 'success' && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <CheckCircle className="h-16 w-16 text-green-600" />
                <p className="text-lg font-medium">Submission Confirmed!</p>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  Your Xactus credentials request was successfully received. You should receive confirmation shortly.
                </p>
              </div>
            )}

            {xactusStatus === 'manual' && xactusIframeUrl && (
              <div className="space-y-3">
                {/* Success indicator showing form was pre-filled */}
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                        Form Pre-filled Successfully!
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-400">
                        All your information has been automatically filled in the form below.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Instructions for manual submission */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">
                        Final Step Required:
                      </p>
                      <ol className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-decimal list-inside">
                        <li>Scroll down to review the pre-filled information</li>
                        <li>Click the <span className="font-semibold">"Submit"</span> button at the bottom of the form</li>
                        <li>Wait for the confirmation message from Xactus</li>
                      </ol>
                    </div>
                  </div>
                </div>

                {/* Iframe with the pre-filled form */}
                <iframe
                  id="xactus-iframe"
                  src={xactusIframeUrl}
                  className="w-full h-[600px] border rounded-md"
                  title="Xactus Credentials Request Form"
                />
              </div>
            )}

            {xactusStatus === 'error' && (
              <div className="flex flex-col items-center justify-center py-12 space-y-4">
                <AlertTriangle className="h-16 w-16 text-red-600" />
                <p className="text-lg font-medium">Submission Failed</p>
                <p className="text-sm text-muted-foreground text-center max-w-md">
                  There was an error submitting your request. Please try again or contact support if the issue persists.
                </p>
              </div>
            )}
          </div>

          {xactusStatus === 'manual' && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setXactusModalOpen(false);
                  setXactusSubmitting(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {hasPendingChanges && (
        <div className="lw-user-profile__pending-changes">
          <PendingChangesCard
            changes={pendingChanges}
            onSave={handleSubmit(onSubmit, handleInvalidSubmit)}
            onDiscard={handleDiscard}
            isSaving={isSaving}
            position="relative"
            presentation="profile"
          />
        </div>
      )}

      {!hasPendingChanges && activeTab !== 'compensation' && activeTab !== 'documents' && (
      <div className="lw-user-profile__save-region">
        <p className="text-sm text-[var(--ink-soft)]">
          All changes saved <span aria-hidden="true">✓</span>
        </p>

        <Button type="submit" disabled={isSaving || updating || !hasPendingChanges}>
            {isSaving || updating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save changes'
            )}
          </Button>
      </div>
      )}
    </form>

    {/* OB-driven Change Plan dialog (plan v5). Only renders when ob_compensation_feature is on. */}
    {obCompFeatureEnabled && (userId ?? user?.id) && (
      <ObChangePlanDialog
        open={obChangePlanDialogOpen}
        onOpenChange={setObChangePlanDialogOpen}
        targetUserId={(userId ?? user!.id) as string}
        current={currentObPlan}
        onSubmitted={async () => {
          // Refetch the profile (admin: target user; self: signed-in user).
          // The widened OB feature/open-request effect re-fires on profile
          // change and refreshes obOpenRequest automatically — no manual
          // request-query needed here.
          if (userId) {
            const { UserService } = await import('@/services');
            const { data: fresh } = await UserService.getUser(userId, supabase);
            if (fresh) {
setSpecificProfile(fresh);
}
          } else {
            await currentProfileHook.refetch();
          }
        }}
      />
    )}

    {/* Admin-only first-time setup dialog. Mounted in admin view alongside the
        Change Plan dialog so both share the same OB schedule catalog source. */}
    {obCompFeatureEnabled && isAdminView && profile?.id && (
      <ObInitialAssignDialog
        open={obInitialAssignOpen}
        onOpenChange={setObInitialAssignOpen}
        targetUserId={profile.id}
        targetUserName={[profile.firstName, profile.lastName].filter(Boolean).join(' ') || undefined}
        onAssigned={async () => {
          // After a successful assign + audit, refetch the profile so the cache
          // fields and freshness timestamp render fresh, then run inspect to
          // refresh local state (clearing any stale needs_review panel).
          if (userId) {
            const { UserService } = await import('@/services');
            const { data: fresh } = await UserService.getUser(userId, supabase);
            if (fresh) {
setSpecificProfile(fresh);
}
          } else {
            await currentProfileHook.refetch();
          }
          if (profile?.id) {
            try {
              const result = await inspectObUser(profile.id);
              setObInspectResult(result);
            } catch (e) {
              log.warn('post-assign inspect refresh failed', {}, e as Error);
            }
          }
        }}
      />
    )}

    {/* Compensation Change Request Modal (legacy) */}
    <Dialog open={requestModalOpen} onOpenChange={setRequestModalOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Compensation Change</DialogTitle>
          <DialogDescription>
            Changes will take effect on {calculateEffectiveDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Banked Compensation */}
          <div className="space-y-2">
            <Label htmlFor="requested_banked_compensation">Banked Compensation (%)</Label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground min-w-[100px]">
                Current: {compensationData.banked_compensation ? `${compensationData.banked_compensation}%` : 'Not set'}
              </span>
              <span className="text-muted-foreground">→</span>
              <Select
                value={requestForm.bankedCompensation?.toFixed(2) || ''}
                onValueChange={(v) => setRequestForm(prev => ({
                  ...prev, bankedCompensation: v ? parseFloat(v) : null
                }))}
              >
                <SelectTrigger id="requested_banked_compensation" className="w-28">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent className="lw-ds">
                  {BANKED_COMPENSATION_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Maximum Compensation */}
          <div className="space-y-2">
            <Label htmlFor="requested_maximum_compensation">Maximum Compensation</Label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground min-w-[100px]">
                Current: {compensationData.maximum_compensation ? `$${compensationData.maximum_compensation.toLocaleString()}` : 'Not set'}
              </span>
              <span className="text-muted-foreground">→</span>
              <div className="relative w-28">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <Input
                id="requested_maximum_compensation"
                  type="text"
                  className="pl-8"
                  value={requestForm.maximumCompensation?.toLocaleString() || ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setRequestForm(prev => ({
                      ...prev, maximumCompensation: value ? parseInt(value) : null
                    }));
                  }}
                />
              </div>
            </div>
          </div>

          {/* Justification */}
          <div className="space-y-2">
            <Label htmlFor="compensation_request_justification">Justification *</Label>
            <Textarea
              id="compensation_request_justification"
              placeholder="Explain why you're requesting this change..."
              value={requestForm.justification}
              onChange={(e) => setRequestForm(prev => ({
                ...prev, justification: e.target.value
              }))}
              rows={4}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setRequestModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitCompensationRequest}
            disabled={submittingRequest || !requestForm.justification.trim() || (
              requestForm.bankedCompensation === compensationData.banked_compensation &&
              requestForm.maximumCompensation === compensationData.maximum_compensation
            )}
          >
            {submittingRequest ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Request'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
